/**
 * Cálculo da criação de rotas da Roteirização usado pela tela (Roteirizacao.vue, que espalha
 * estes métodos no próprio `methods`).
 *
 * ATENÇÃO: o pipeline do scheduler (estágio ROTAS) roda o PORTE PYTHON desta lógica, em
 * monitor/scheduler/pythonScripts/verso/routing/methods.py (contexto em routing/context.py,
 * equivalente ao `this` do componente). Toda mudança de lógica aqui precisa ir para lá também,
 * para o pipeline continuar devolvendo exatamente o que a tela calcula.
 *
 * Os métodos leem configuração e estado por `this` (destination, initialBase, maxTripDuration,
 * stopServiceConfig, ...) e usam `this.$t`, `this.$set`, `this.$nextTick` e `this.showError`.
 * O transporte ao Verso é de quem hospeda: `_sendFleetSolve`, `_requestVersoPlanScheduled` e
 * `_summarizeAutoFleet` (na tela, pelo scheduler/API; no worker, direto no processo).
 *
 * CommonJS de propósito: os testes Node fazem require e o webpack da tela importa normalmente.
 */
'use strict';

const { decodeAddress } = require('../roteirizacaoAddressMatch');
const { decodeToLatLngs, encodeLatLngs } = require('./polylineEncoded');
const { calcularSharePercentOperacional } = require('./fleetBalanco');

/** Faixa de ids de veículo por grupo quando há grupos concorrentes (ids não podem colidir). */
const CONCURRENT_GROUP_VEHICLE_ID_SPAN = 1000;
/**
 * Reuso de veículo: fim da rota anterior e início da próxima a até esta distância contam como
 * o mesmo local (basta o tempo mínimo de transferência); acima disso, vale o deslocamento.
 */
const REUSE_SAME_LOCATION_M = 100;
/**
 * Caminhadas e reposições pelo scheduler (VERSO_PLAN auxiliar): prazo por job. Na tela a fila
 * serial de chunks de 100 com 15 min cada deixava o loading preso após o último grupo.
 */
const VERSO_PLAN_AUX_WAIT_MS = 180000;

/**
 * Campos de data/computed da tela lidos pelos métodos compartilhados: a tela os manda no
 * payload do pipeline (`config`) e o worker os põe no contexto.
 */
const ROUTING_CONFIG_FIELDS = [
    'destination',
    'initialBase',
    'initialBaseStop',
    'maxTripDuration',
    'tempoReusoVeiculos',
    'stopServiceConfig',
    'alightServiceConfig',
    'isFleetAutomaticMode',
    'isFleetManualMode',
    'isRoutingOperationalMode',
    'manualFleetEntries',
    'vehicleTypeRows',
    'operationalVehicleTypeRows',
    'selectedOperationalVehicleTypes',
    'operationalFleetPercentOverrides',
    'selectedAvailableVehicleType',
    'simulationFleetCosts',
    'radiusConfig',
    'destinationShifts',
    'passengerShiftsById',
    'countries',
];

/** Chaves de texto usadas pelos métodos compartilhados (a tela manda os modelos traduzidos). */
const ROUTING_LABEL_KEYS = [
    'routePlanning.direction.displacement',
    'routePlanning.direction.ida',
    'routePlanning.direction.volta',
    'routePlanning.fields.stop',
    'routePlanning.fields.stopNumber',
    'routePlanning.loading.sendError',
    'routePlanning.turnos.noMatch',
    'routePlanning.validation.availableVehicleTypesRequired',
    'routePlanning.validation.clusterizacaoFalhou',
    'routePlanning.validation.destinationIsStop',
    'routePlanning.validation.initialBaseIsStop',
    'routePlanning.validation.noRouteInDesiredTime',
    'routePlanning.validation.noRoutesFound',
    'routePlanning.validation.noStopsGeocoded',
    'routePlanning.validation.passengersExceedFleetCapacity',
    'routePlanning.validation.rateLimit',
    'routePlanning.validation.vehicleCountGreaterThanZero',
];

/** Distância em metros entre dois pontos WGS84 (mesma fórmula de src/utils/haversine.js). */
function haversineMeters(lat1, lng1, lat2, lng2) {
    const aLat = Number(lat1);
    const aLng = Number(lng1);
    const bLat = Number(lat2);
    const bLng = Number(lng2);
    if (![aLat, aLng, bLat, bLng].every(Number.isFinite)) return NaN;
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Segundos a partir de número ou string estilo VROOM (ex.: "3600s"). */
function parseDurationSeconds(value) {
    if (value == null) return NaN;
    if (typeof value === 'string') return parseInt(String(value).replace(/s$/i, ''), 10);
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
}

/** Inteiro positivo (>= 1). Substitui `Number.isFinite(n) && n >= 1 && n % 1 === 0`. */
function isPositiveInt(value) {
    const n = Number(value);
    return Number.isFinite(n) && n >= 1 && n % 1 === 0;
}

/**
 * Converte par lat/lng rejeitando null/undefined/'' (Number(null)===0).
 * Também rejeita (0,0), que nunca é uma cerca válida neste produto.
 */
function parseLatLngPair(latRaw, lngRaw) {
    if (latRaw == null || lngRaw == null || latRaw === '' || lngRaw === '') return null;
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    return { lat, lng };
}

/**
 * Service de uma parada, em segundos.
 * ESPELHO de totalServiceTime() em api/routers/verso/getsolve.js.
 * @param {{mode:'fixo'|'variavel', fixedSec:number, perPassengerSec:number}} cfg
 */
function jobServiceSeconds(passengerAmount, cfg) {
    const c = cfg || { mode: 'variavel', fixedSec: 0, perPassengerSec: 0 };
    return c.mode === 'fixo'
        ? c.fixedSec
        : c.perPassengerSec * Math.max(1, parseInt(String(passengerAmount), 10) || 1);
}

const routingMethods = {
    _compareScheduleHHmm(a, b) {
        if (a === b) return 0;
        if (a == null || String(a).trim() === '') return 1;
        if (b == null || String(b).trim() === '') return -1;
        const ma = this._timeToMinutes(a);
        const mb = this._timeToMinutes(b);
        if (ma == null && mb == null) return 0;
        if (ma == null) return 1;
        if (mb == null) return -1;
        return ma - mb;
    },
    /** Chave de horário de um grupo no mapa do pré-cluster (chegada na ida, saída na volta). */
    _preclusterScheduleKey(g, isVolta) {
        const raw = isVolta ? g.horarioSaida : g.arrivalTime;
        return raw == null || raw === '' ? '__sem__' : String(raw);
    },
    /**
     * Aplica o resultado da clusterização de um sentido ao plano do pré-cluster
     * (`planPreclusterGroups`, na tela): reagrupa as paradas clusterizadas por horário e
     * completa o mapa grupo -> paradas. Usado pela tela e pelo estágio ROTAS do pipeline.
     * @returns {Promise<{ map: Object, clusterTypeLocal: string|null, error?: boolean }>}
     */
    async finishPreclusterGroups(plan, resultado) {
        const { isVolta } = plan;
        const map = Object.assign(Object.create(null), plan.map);
        let clusterTypeLocal = null;
        if (!plan.toCluster || !plan.toCluster.length) return { map, clusterTypeLocal };

        // Sem paradas a clusterizar não há request; PCD já vai no request (centro fixo).
        const res = plan.cluster
            ? await this.applyCluster(resultado, plan.cluster.initialStops, null, null, plan.pcdStops)
            : { currentStops: [...(plan.pcdStops || [])], clusterTypeLocal: null };
        if (res.error) {
            return { error: true, map, clusterTypeLocal };
        }
        if (res.clusterTypeLocal) {
            clusterTypeLocal = res.clusterTypeLocal;
        }

        const clustered = res.currentStops || [];
        const regrouped = isVolta
            ? this.groupStopsByDeparture(clustered, { requireDeparture: true })
            : this.groupStopsByArrival(clustered, { requireArrival: true });

        for (const g of regrouped) {
            map[this._preclusterScheduleKey(g, isVolta)] = g.stops;
        }
        for (const g of plan.toCluster) {
            const key = this._preclusterScheduleKey(g, isVolta);
            if (map[key] == null) {
                map[key] = g.stops || [];
            }
        }
        return { map, clusterTypeLocal };
    },
    /**
     * Cálculo de um grupo de horário (mesmo horário de chegada/saída): clusterização já feita,
     * validação, solve de frota, plan com tempo máximo, normalização e, já em ordem cronológica
     * (`commitGate`), consumo da frota manual e registro no pool de reuso de veículos.
     * Devolve o resultado final do grupo quando ele termina aqui (erro, soft-fail) ou
     * `{ __apply }` com o que a tela precisa para desenhar as rotas (Roteirizacao.vue
     * `applyRouteGroup`). Não toca em estado de tela.
     */
    async computeRouteGroup(stopGroup, arrivalTimeGrupo, voltaOpts = null, groupOpts = null) {
        if (![...stopGroup].length) {
            return { stopsFinal: [], warnings: [] };
        }

        const isVolta = voltaOpts != null;
        const horarioSaidaGrupo = isVolta ? voltaOpts.horarioSaida : null;
        const hasGarage = this.initialBaseStop == 1;
        let hasStartBase = !isVolta && hasGarage;
        let hasEndBase = isVolta && hasGarage;
        const startBaseEndereco = this.initialBase && (this.initialBase.endereco || `${this.initialBase.lat_centro}, ${this.initialBase.lng_centro}`);

        const warnings = [];
        const isRoundTripGroup = !!(groupOpts && groupOpts.isRoundTrip);
        // baseRotaIdx/baseHorarioIdx/baseTabelaIdx são lidos só na fase de aplicar (depois de
        // `commitGate.enter()`), quando o estado da tela já reflete os grupos anteriores.


        let currentStops = null;
        let clusterTypeLocal = null;

        const cache = this._preclusterBySchedule;
        const cacheSide = cache
            ? (isVolta ? cache.volta : cache.ida)
            : null;
        const cacheKeyRaw = isVolta ? horarioSaidaGrupo : arrivalTimeGrupo;
        const cacheKey = cacheKeyRaw == null || cacheKeyRaw === '' ? '__sem__' : String(cacheKeyRaw);
        if (cacheSide && Object.prototype.hasOwnProperty.call(cacheSide, cacheKey)) {
            currentStops = cacheSide[cacheKey] || [];
            clusterTypeLocal = cache.clusterTypeLocal || null;
        } else {
            currentStops = [...(stopGroup || [])];
        }
        // Sem cluster (ou PCD): garante servedAddresses com identidade do CSV para o vínculo
        // original → rota não depender do formattedAddress geocodificado.
        for (const p of currentStops || []) {
            if (!p) continue;
            if (Array.isArray(p.servedAddresses) && p.servedAddresses.length) continue;
            const identityAddr = this.resolveOriginalPointAddress(p);
            if (identityAddr) {
                this.$set(p, 'servedAddresses', [identityAddr]);
            }
        }
        const didCluster = currentStops !== stopGroup
            && (currentStops.length !== (stopGroup || []).length
                || currentStops.some((s, i) => s !== stopGroup[i]));
        await this.$nextTick();

        const stops = this.mapStops(currentStops);
        const notGeocodedCount = (currentStops || []).filter((s) => !this.hasStopCoords(s)).length
            || Math.max(0, (stopGroup || []).length - (currentStops || []).length);
        const valRes = this.validateStops(stops, { notGeocodedCount });
        if (valRes) return valRes;

        const totalPassengers = stops.reduce((sum, s) => sum + (Number(s.passengerAmount) || 0), 0);

        const autoFleetMode = this.isFleetAutomaticMode;

        const { maxTripSec, idaStartBase, idaEndBase, startBase, endBase } = this.tripBases(isVolta, arrivalTimeGrupo);

        let solveStops;
        let numVeiculosSolve;
        let numVeiculosUserMax = null;
        const vehicleSpecById = {};
        let precomputedVersoBody = null;
        let precomputedSolvePayload = null;
        let pickedFleetPartialMeta = null;

        const usesVehicleReusePool = !!(groupOpts && groupOpts.reusePool);
        const reusePool = usesVehicleReusePool ? groupOpts.reusePool : null;
        const maxVehicleTrips = this.countPassengerTurnosFromStops(stops);
        let reuseFromForSolve = null;
        let vehicleIdStartForSolve = null;
        if (reusePool) {
            // A volta começa no CD (destino): ponto de início da rota para a regra de reuso.
            const cdPoint = idaEndBase ? { lat: Number(idaEndBase[1]), lng: Number(idaEndBase[0]) } : null;
            if (isVolta) {
                let garageReturnVehicleIds = [];
                if (hasGarage && groupOpts && groupOpts.scheduleQueue && groupOpts.scheduleIndex != null) {
                    const futureItems = groupOpts.scheduleQueue.slice(groupOpts.scheduleIndex + 1);
                    const flags = await reusePool.computeGarageReturnFlags({
                        futureItems,
                        maxTripSec,
                        voltaDeparture: horarioSaidaGrupo,
                        voltaStops: stopGroup,
                        cdPoint,
                    });
                    garageReturnVehicleIds = flags.garageReturnVehicleIds;
                }
                reuseFromForSolve = await reusePool.getReuseFrom({
                    nextLeg: 'volta',
                    nextDeparture: horarioSaidaGrupo,
                    nextStartPoint: cdPoint,
                });
                if (reuseFromForSolve && hasGarage) {
                    hasEndBase = false;
                    if (garageReturnVehicleIds.length) {
                        const reuseIds = new Set(
                            (reuseFromForSolve.vehicles || []).map((v) => Number(v.id)),
                        );
                        reuseFromForSolve.garageReturnVehicleIds = garageReturnVehicleIds
                            .filter((id) => reuseIds.has(Number(id)));
                    } else if (!groupOpts || groupOpts.scheduleQueue == null) {
                        reuseFromForSolve.garageReturnVehicleIds = (reuseFromForSolve.vehicles || [])
                            .map((v) => Number(v.id))
                            .filter(Number.isFinite);
                    }
                }
            } else if (arrivalTimeGrupo) {
                const idaDepartureEstimate = this.offsetTime(arrivalTimeGrupo, maxTripSec, -1);
                const firstIdaStop = stops.length
                    ? { lat: Number(stops[0].lat), lng: Number(stops[0].lng) }
                    : null;
                reuseFromForSolve = await reusePool.getReuseFrom({
                    nextLeg: 'ida',
                    nextDeparture: idaDepartureEstimate,
                    nextStartPoint: firstIdaStop,
                });
                if (reuseFromForSolve && hasGarage && reuseFromForSolve.vehicles && reuseFromForSolve.vehicles.length) {
                    reuseFromForSolve.skipGarageDepartureVehicleIds = reuseFromForSolve.vehicles
                        .map((v) => Number(v.id))
                        .filter(Number.isFinite);
                }
            }
            // Reserva a faixa de ids mesmo quando há reuso: os veículos
            // suplementares não podem receber ids de veículos físicos já no pool.
            vehicleIdStartForSolve = reusePool.nextVehicleIdStart();
            if (groupOpts && groupOpts.concurrentGroups && groupOpts.scheduleIndex != null) {
                // Grupos concorrentes não veem o que o outro vai reservar: faixa própria por grupo.
                vehicleIdStartForSolve = Math.max(
                    vehicleIdStartForSolve,
                    groupOpts.scheduleIndex * CONCURRENT_GROUP_VEHICLE_ID_SPAN + 1,
                );
            }
        }

        const fleetSolveCtxBase = {
            startBase,
            endBase,
            maxTripSec,
            arrivalTime: arrivalTimeGrupo,
            hasStartBase,
            hasEndBase,
            isVolta,
            vehicleSpecById,
            maxVehicleTrips,
            reuseFrom: reuseFromForSolve,
            vehicleIdStart: vehicleIdStartForSolve,
        };

        if (!autoFleetMode) {
            const fleetPlan = this.resolveManualFleetPlan();
            if (!fleetPlan.entries || !fleetPlan.entries.length || !isPositiveInt(fleetPlan.numVeiculosUser)) {
                this.showError({
                    message: this.$t('routePlanning.validation.vehicleCountGreaterThanZero'),
                });
                return { error: true };
            }
            const fleetEntriesForGroup = (isRoundTripGroup || usesVehicleReusePool)
                ? fleetPlan.entries
                : ((groupOpts && groupOpts.manualFleetRemaining && !isVolta)
                    ? groupOpts.manualFleetRemaining.filter((entry) => isPositiveInt(entry.count))
                    : fleetPlan.entries);
            const fleetCountForGroup = fleetEntriesForGroup.reduce((sum, e) => sum + e.count, 0);
            if (!fleetCountForGroup) {
                this.showError({
                    message: this.$t('routePlanning.validation.passengersExceedFleetCapacity'),
                });
                return {
                    error: true,
                    failureReason: 'INSUFFICIENT_FLEET',
                    unassignedStops: this.buildUnassignedStopsFromSourceStops(currentStops),
                };
            }
            const totalFleetCapacityGrupo = fleetEntriesForGroup.reduce(
                (sum, e) => sum + (e.count * e.capacity),
                0,
            );
            if (totalPassengers > totalFleetCapacityGrupo) {
                this.showError({
                    message: this.$t('routePlanning.validation.passengersExceedFleetCapacity'),
                });
                return {
                    error: true,
                    failureReason: 'INSUFFICIENT_FLEET',
                    unassignedStops: this.buildUnassignedStopsFromSourceStops(currentStops),
                };
            }
            numVeiculosUserMax = fleetPlan.numVeiculosUser;
            const vehicleIdOffset = (isRoundTripGroup || usesVehicleReusePool)
                ? 0
                : this._getManualFleetVehicleIdOffset();
            const pickedFleet = await this.requestFleetSolve(stops, {
                ...fleetSolveCtxBase,
                fleetTypes: this.getManualFleetTypesForOptimizer(fleetEntriesForGroup),
            });
            if (pickedFleet.error) {
                return {
                    error: true,
                    failureReason: pickedFleet.failureReason || null,
                    failureDetail: pickedFleet.failureDetail || null,
                    rateLimited: !!pickedFleet.rateLimited,
                    // Sem detalhamento do backend, o grupo inteiro vira não roteirizado:
                    // é grosseiro, mas nunca some parada sem deixar rastro na aba.
                    unassignedStops: pickedFleet.unassignedStops
                        || this.buildUnassignedStopsFromSourceStops(currentStops),
                };
            }
            if (vehicleIdOffset) {
                this._offsetManualFleetVehicleIds({
                    vehicleSpecById,
                    precomputedVersoBody: pickedFleet.precomputedVersoBody,
                    precomputedSolvePayload: pickedFleet.precomputedSolvePayload,
                }, vehicleIdOffset);
            }
            ({
                solveStops,
                numVeiculosSolve,
                precomputedVersoBody,
                precomputedSolvePayload,
            } = pickedFleet);
            this.attachServedAddressesToSolveStops(solveStops, stops);
        } else {
            const pickedFleet = await this.requestFleetSolve(stops, fleetSolveCtxBase);
            if (pickedFleet.error) {
                return {
                    error: true,
                    failureReason: pickedFleet.failureReason || null,
                    failureDetail: pickedFleet.failureDetail || null,
                    rateLimited: !!pickedFleet.rateLimited,
                    // Frota automática que estoura o tempo máximo volta 422 sem
                    // `unassignedStops` (ninguém fica unassigned; nenhuma rota é aceita).
                    // Sem este fallback o grupo inteiro sumia da tela.
                    unassignedStops: pickedFleet.unassignedStops
                        || this.buildUnassignedStopsFromSourceStops(currentStops),
                };
            }
            // Soft-fail total: tempo máximo impediu qualquer rota — só registra não-alocados.
            if (pickedFleet.partialAllocation && !pickedFleet.precomputedSolvePayload) {
                return {
                    stopsFinal: [],
                    warnings,
                    unassignedStops: pickedFleet.unassignedStops || [],
                    unassignedPassengerCount: pickedFleet.unassignedPassengerCount,
                    softUnassignedOnly: true,
                    solveStops: pickedFleet.solveStops || null,
                };
            }
            ({
                solveStops,
                numVeiculosSolve,
                precomputedVersoBody,
                precomputedSolvePayload,
            } = pickedFleet);
            this.attachServedAddressesToSolveStops(solveStops, stops);
            pickedFleetPartialMeta = {
                partialAllocation: !!pickedFleet.partialAllocation,
                unassignedStops: pickedFleet.unassignedStops || null,
                unassignedPassengerCount: pickedFleet.unassignedPassengerCount,
            };
        }

        const jobs = this.buildJobs(solveStops, isVolta);

        if (!precomputedSolvePayload || !precomputedVersoBody) {
            this.showError({ message: this.$t('routePlanning.loading.sendError') });
            return { error: true };
        }

        const bodyVehicles = Array.isArray(precomputedVersoBody.vehicles)
            ? precomputedVersoBody.vehicles
            : [];
        const bodyJobs = Array.isArray(precomputedVersoBody.jobs)
            ? precomputedVersoBody.jobs
            : jobs;

        const versoBody = { jobs: bodyJobs, vehicles: bodyVehicles };
        let versoPayload = precomputedSolvePayload;
        const solvePayload = precomputedSolvePayload;
        const numVeiculos = numVeiculosSolve != null ? numVeiculosSolve : bodyVehicles.length;

        // Unassigned do solve (com servedAddresses) — tabela de não-alocadas; esses jobs não vão ao /plan.
        let softUnassignedStops = (
            pickedFleetPartialMeta
            && Array.isArray(pickedFleetPartialMeta.unassignedStops)
            && pickedFleetPartialMeta.unassignedStops.length
        )
            ? pickedFleetPartialMeta.unassignedStops
            : this.buildUnassignedStopsFromPayload(solvePayload, solveStops, currentStops);
        let softUnassignedPassengerCount = (softUnassignedStops || []).reduce((sum, s) => {
            const n = Number(s && (s.passageiros != null ? s.passageiros : s.passengerAmount));
            return sum + (Number.isFinite(n) && n > 0 ? n : 1);
        }, 0);
        if (!softUnassignedPassengerCount && pickedFleetPartialMeta
            && pickedFleetPartialMeta.unassignedPassengerCount != null) {
            softUnassignedPassengerCount = Number(pickedFleetPartialMeta.unassignedPassengerCount) || 0;
        }

        versoPayload = await this.tryPlan({
            jobs,
            versoBody,
            versoPayload,
            solveStops,
            numVeiculos,
            hasStartBase,
            hasEndBase,
            isVolta,
            maxTripSec,
            skipGarageDepartureVehicleIds: reuseFromForSolve
                ? reuseFromForSolve.skipGarageDepartureVehicleIds
                : null,
            garageReturnVehicleIds: reuseFromForSolve
                ? reuseFromForSolve.garageReturnVehicleIds
                : null,
            repositionStartVehicleIds: this._repositionStartVehicleIds(reuseFromForSolve),
        });

        if (versoPayload && versoPayload.error && versoPayload.failureReason === 'SERVER_ERROR') {
            // /plan indisponível após os retries: descarta o solve e marca o grupo inteiro
            // como não roteirizado, com o erro do servidor como motivo.
            return {
                ...versoPayload,
                unassignedStops: this.buildUnassignedStopsFromAllSolveStops(solveStops, currentStops),
                solveStops,
            };
        }

        if (versoPayload && versoPayload.error) {
            // O /plan só reordena/detalha o que o /solve já resolveu. Se ele falha (ex.: 422
            // por estourar o tempo máximo por poucos segundos, por diferença de cálculo entre
            // solve e plan), o resultado do solve continua válido — o backend já o validou
            // contra maxTripSec — então usa ele em vez de descartar o grupo inteiro.
            // `assertFeasible` logo abaixo revalida com o cálculo do próprio cliente, então
            // uma rota realmente inviável ainda é barrada aqui.
            const solveHasRoutes = !!(solvePayload
                && Array.isArray(solvePayload.routes)
                && solvePayload.routes.length);
            if (solveHasRoutes) {
                versoPayload = solvePayload;
            } else {
                const hasKnownUnassigned = (softUnassignedStops && softUnassignedStops.length)
                    || (Array.isArray(versoPayload.unassignedStops) && versoPayload.unassignedStops.length);
                return {
                    ...versoPayload,
                    unassignedStops: hasKnownUnassigned
                        ? (softUnassignedStops && softUnassignedStops.length
                            ? softUnassignedStops
                            : versoPayload.unassignedStops)
                        // Falha dura sem detalhamento (ex.: /plan 422 por tempo máximo de
                        // viagem, capturado no catch de tryPlan): marca o grupo inteiro como
                        // não-alocado em vez de deixá-lo desaparecer sem rastro.
                        : this.buildUnassignedStopsFromAllSolveStops(solveStops, currentStops),
                    solveStops,
                };
            }
        }

        const feasibleRes = this.assertFeasible(versoPayload, maxTripSec, {
            hasStartBase,
            hasEndBase,
            isVolta,
            skipGarageDepartureVehicleIds: reuseFromForSolve
                ? reuseFromForSolve.skipGarageDepartureVehicleIds
                : null,
            garageReturnVehicleIds: reuseFromForSolve
                ? reuseFromForSolve.garageReturnVehicleIds
                : null,
            repositionStartVehicleIds: this._repositionStartVehicleIds(reuseFromForSolve),
            solveStops,
            sourceStops: currentStops,
            // Parada que o VROOM não conseguiu encaixar nunca invalida o grupo: as rotas
            // que ele montou são entregues e as sobras vão para a aba de não roteirizadas.
            // Antes só a frota automática fazia isso; na manual o grupo inteiro era
            // descartado, e uma parada sem encaixe apagava rotas perfeitamente válidas.
            allowUnassigned: true,
        });
        if (feasibleRes) {
            if (feasibleRes.error) {
                return {
                    ...feasibleRes,
                    solveStops,
                    unassignedStops: softUnassignedStops && softUnassignedStops.length
                        ? softUnassignedStops
                        : (feasibleRes.unassignedStops || null),
                };
            }
            if (feasibleRes.versoPayload) versoPayload = feasibleRes.versoPayload;
            if (feasibleRes.partialUnassigned && Array.isArray(feasibleRes.unassignedStops)
                && feasibleRes.unassignedStops.length) {
                const seen = new Set((softUnassignedStops || []).map((u) => u && u.jobId));
                const extras = feasibleRes.unassignedStops.filter((u) => !seen.has(u && u.jobId));
                softUnassignedStops = [...(softUnassignedStops || []), ...extras];
                softUnassignedPassengerCount = softUnassignedStops.reduce((sum, u) => {
                    const n = Number(u && (u.passageiros != null ? u.passageiros : u.passengerAmount));
                    return sum + (Number.isFinite(n) && n > 0 ? n : 1);
                }, 0);
            }
        }

        const fin = await this.finalizeRoutes({
            versoPayload,
            solvePayload,
            solveStops,
            numVeiculos,
            hasStartBase,
            hasEndBase,
            isVolta,
            autoFleetMode,
            vehicleSpecById,
            displacementFlags: reuseFromForSolve ? {
                skipGarageDepartureVehicleIds: reuseFromForSolve.skipGarageDepartureVehicleIds,
                garageReturnVehicleIds: reuseFromForSolve.garageReturnVehicleIds,
                repositionStartVehicleIds: this._repositionStartVehicleIds(reuseFromForSolve),
            } : null,
        });
        if (fin.error) {
            return {
                error: true,
                solveStops,
                // Aqui já existe solveStops, então o grupo inteiro é descrito a partir dele
                // em vez de sumir da aba quando não há unassigned detalhado.
                unassignedStops: softUnassignedStops && softUnassignedStops.length
                    ? softUnassignedStops
                    : this.buildUnassignedStopsFromAllSolveStops(solveStops, currentStops),
                unassignedPassengerCount: softUnassignedPassengerCount || null,
            };
        }
        const { responseData } = fin;

        // ---- A partir daqui só um grupo por vez, em ordem cronológica: o consumo da frota
        // manual e o pool de reuso dependem dos grupos anteriores. ----
        if (groupOpts && groupOpts.commitGate) await groupOpts.commitGate.enter();

        if (!autoFleetMode && !isRoundTripGroup && !usesVehicleReusePool && !isVolta
            && groupOpts && groupOpts.manualFleetRemaining) {
            this._consumeManualFleetRemaining(
                groupOpts.manualFleetRemaining,
                responseData,
                vehicleSpecById,
            );
        }

        if (reusePool) {
            const reuseStateByVehicle = this._buildVehicleReuseState(responseData, {
                isVolta,
                arrivalTimeGrupo,
                horarioSaidaGrupo,
                hasStartBase,
                hasEndBase,
                hasGarage,
                maxTripSec,
                idaStartBase,
                idaEndBase,
            });
            reusePool.registerFromSolve(
                { precomputedSolvePayload: solvePayload, precomputedVersoBody: versoBody },
                vehicleSpecById,
                reuseStateByVehicle,
                { isVolta },
            );
        }

        return {
            __apply: {
                responseData,
                currentStops,
                didCluster,
                clusterTypeLocal,
                vehicleSpecById,
                autoFleetMode,
                numVeiculos,
                numVeiculosUserMax,
                isVolta,
                arrivalTimeGrupo,
                horarioSaidaGrupo,
                idaStartBase,
                idaEndBase,
                hasStartBase,
                hasEndBase,
                startBaseEndereco,
                softUnassignedStops,
                softUnassignedPassengerCount,
                solveStops,
            },
        };
    },
    /** Parada com latitude e longitude numéricas finitas (rejeita '', null e (0,0)). */
    hasStopCoords(p) {
        if (!p) return false;
        return !!parseLatLngPair(
            p.latitude != null ? p.latitude : p.lat,
            p.longitude != null ? p.longitude : p.lng,
        );
    },
    getSelectedSimulationFleetTypes() {
        const selected = new Set((this.selectedAvailableVehicleType || []).map((id) => String(id)));
        return (this.vehicleTypeRows || [])
            .filter((row) => selected.has(String(row.id_tipo_veiculo)))
            .map((row) => {
                const config = this.simulationFleetCosts[String(row.id_tipo_veiculo)] || {};
                const hasShare = config.percent != null && config.percent !== '';
                return {
                    ...(hasShare ? { sharePercent: Number(config.percent) || 0 } : {}),
                    key: String(row.id_tipo_veiculo),
                    type: row.descricao_tipo_veiculo != null
                        ? String(row.descricao_tipo_veiculo).trim()
                        : String(row.id_tipo_veiculo),
                    capacity: Math.max(1, parseInt(String(config.capacity), 10) || 0),
                    fixedCost: Number(config.fixedCost) || 0,
                    costPerKm: Number(config.costPerKm) || 0,
                    laborCost: Number(config.laborCost) || 0,
                    profile: 'car',
                };
            })
            .filter((row) => row.type && row.capacity > 0);
    },
    /** Texto exibido no marcador/tooltip de uma parada (endereço ou coordenadas). */
    getStopMarkerName(parada) {
        if (!parada) return this.$t('routePlanning.fields.stop');
        const endereco = parada.endereco && typeof parada.endereco === 'object' && parada.endereco.type === 'Buffer'
            ? decodeAddress(parada.endereco)
            : String(parada.endereco || '').trim();
        if (endereco) return endereco;
        return `${this.$t('routePlanning.fields.stop')} (${parada.latitude}, ${parada.longitude})`;
    },
    /**
     * Monta uma string de endereço a partir de um objeto parada (ex.: linha de pessoas_enderecos).
     * Campos varbinary/Buffer são decodificados para string.
     */
    buildAddressFromStop(parada, countryList) {
        if (!parada) return '';
        const list = countryList || this.countries || [];
        const str = (v) => {
            if (v == null) return '';
            if (typeof v === 'object' && v.type === 'Buffer' && Array.isArray(v.data)) {
                const decoded = decodeAddress(v);
                return decoded != null ? String(decoded).trim() : '';
            }
            return String(v).trim();
        };
        const logradouro = str(parada.logradouro);
        const numero = str(parada.numero);
        const bairro = str(parada.bairro);
        const cidade = str(parada.cidade);
        const estado = str(parada.estado);
        const cep = str(parada.cep);
        let nomePais = '';
        if (parada.id_pais != null && list.length) {
            const pais = list.find(p => p.id_pais === Number(parada.id_pais));
            nomePais = pais ? (pais.nome || pais.nombre || '') : '';
        }
        const parts = [logradouro, numero, bairro, cidade, estado, cep, nomePais].filter(Boolean);
        return parts.join(', ');
    },
    /** Normaliza horário para HH:mm (aceita HH:mm e HH:mm:ss do MySQL). */
    normalizeHHmmFromCsv(value) {
        if (value == null || String(value).trim() === '') return null;
        const s = String(value).trim();
        const m = s.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/);
        if (!m) return null;
        return `${String(parseInt(m[1], 10)).padStart(2, '0')}:${m[2]}`;
    },
    parseTimeToMinutes(raw) {
        if (raw == null || String(raw).trim() === '') return null;
        const norm = this.normalizeHHmmFromCsv(raw);
        if (!norm) return null;
        const m = norm.match(/^(\d{1,2}):(\d{2})$/);
        if (!m) return null;
        const h = parseInt(m[1], 10);
        const min = parseInt(m[2], 10);
        if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
        return h * 60 + min;
    },
    isTimeInDayWindow(timeMin, startRaw, endRaw) {
        const startMin = this.parseTimeToMinutes(startRaw);
        const endMin = this.parseTimeToMinutes(endRaw);
        if (timeMin == null || startMin == null || endMin == null) return false;
        if (startMin === endMin) return timeMin === startMin;
        if (startMin < endMin) return timeMin >= startMin && timeMin <= endMin;
        return timeMin >= startMin || timeMin <= endMin;
    },
    matchTurnosForTime(rawTime, shifts) {
        const timeMin = this.parseTimeToMinutes(rawTime);
        if (timeMin == null) return [];
        const list = Array.isArray(shifts) ? shifts : (Array.isArray(this.destinationShifts) ? this.destinationShifts : []);
        const days = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
        const matched = [];
        for (const turno of list) {
            if (!turno) continue;
            for (const day of days) {
                const ini = turno[`horario_inicio_${day}`];
                const fim = turno[`horario_fim_${day}`];
                if (ini == null && fim == null) continue;
                if (this.isTimeInDayWindow(timeMin, ini, fim)) {
                    matched.push(turno);
                    break;
                }
            }
        }
        return matched;
    },
    unionTurnosLists(lists) {
        const map = new Map();
        for (const list of lists || []) {
            for (const turno of list || []) {
                if (!turno) continue;
                const key = turno.id_turno != null ? String(turno.id_turno) : JSON.stringify(turno);
                if (!map.has(key)) map.set(key, turno);
            }
        }
        return Array.from(map.values());
    },
    extractShiftTimes(paxTurno, kind) {
        if (!paxTurno) return [];
        const days = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
        const primary = kind === 'volta' ? 'horario_saida_' : 'horario_entrada_';
        const alt = kind === 'volta' ? 'horario_fim_' : 'horario_inicio_';
        const times = new Set();
        for (const day of days) {
            const raw = paxTurno[`${primary}${day}`] ?? paxTurno[`${alt}${day}`];
            if (raw == null || String(raw).trim() === '') continue;
            const n = this.normalizeHHmmFromCsv(String(raw).trim());
            if (n) times.add(n);
        }
        return Array.from(times);
    },
    getTurnosForStop(stop, { sentido = 'ida' } = {}) {
        const routingTurnos = Array.isArray(this.destinationShifts) ? this.destinationShifts : [];
        if (!routingTurnos.length || !stop) return [];

        const raw = sentido === 'volta' ? stop.horario_saida : stop.horario_chegada;
        if (raw != null && String(raw).trim() !== '') {
            const matched = this.matchTurnosForTime(raw, routingTurnos);
            if (matched.length) return matched;
        }

        if (stop.id_turno != null) {
            const paxTurno = this.passengerShiftsById[stop.id_turno];
            if (paxTurno) {
                const kind = sentido === 'volta' ? 'volta' : 'ida';
                const times = this.extractShiftTimes(paxTurno, kind);
                if (times.length) {
                    const matched = this.unionTurnosLists(times.map((t) => this.matchTurnosForTime(t, routingTurnos)));
                    if (matched.length) return matched;
                }
                return [paxTurno];
            }
            if (stop.cod_turno) {
                return [{ cod_turno: stop.cod_turno, id_turno: stop.id_turno }];
            }
        }

        return this.matchTurnosForTime(raw, routingTurnos);
    },
    applyTurnosToStops(stopsList) {
        const applyOne = (stop) => {
            if (!stop) return;
            const ida = this.getTurnosForStop(stop, { sentido: 'ida' });
            const volta = this.getTurnosForStop(stop, { sentido: 'volta' });
            stop.turnosRelacionados = this.formatTurnosLabel(this.unionTurnosLists([ida, volta]));
        };
        if (stopsList) {
            stopsList.forEach(applyOne);
            return;
        }
        (this.stops || []).forEach(applyOne);
        (this.invalidStops || []).forEach(applyOne);
    },
    formatTurnosLabel(turnos) {
        if (!turnos || !turnos.length) return this.$t('routePlanning.turnos.noMatch');
        return turnos.map((turno) => {
            const cod = turno.cod_turno != null ? String(turno.cod_turno).trim() : '';
            const desc = turno.descricao_turno != null ? String(turno.descricao_turno).trim() : '';
            if (cod && desc) return `${cod} - ${desc}`;
            return cod || desc || String(turno.id_turno != null ? turno.id_turno : '');
        }).join(', ');
    },
    pickScheduleFieldFromStopGroup(stopGroup, field) {
        for (const s of stopGroup || []) {
            const raw = s && s[field];
            if (raw != null && String(raw).trim() !== '') {
                return String(raw).trim();
            }
        }
        return '';
    },
    /** Normaliza identificador de país: número positivo ou nome que exista em `countries`. */
    resolveIdPais(value) {
        if (value == null || value === '') return null;
        const v = String(value).trim();
        const num = Number(v);
        if (num != null && num > 0) return num;
        const pais = this.countries.find(p => String(p.nome || p.nombre || '').toLowerCase() === v.toLowerCase());
        return pais ? pais.id_pais : null;
    },
    /**
     * Agrupa paradas pelo horario_chegada normalizado (HH:mm).
     * Paradas sem horário próprio formam um único grupo sem horário de referência.
     * Retorna [{ arrivalTime, stops }] ordenado por horário ascendente (grupo sem horário por último).
     */
    groupStopsByArrival(stops, { requireArrival = false } = {}) {
        const map = new Map();
        for (const p of stops || []) {
            if (requireArrival && (p == null || String(p.horario_chegada || '').trim() === '')) {
                continue;
            }
            const raw = p && p.horario_chegada;
            const chegada = (raw != null && String(raw).trim() !== '')
                ? (this.normalizeHHmmFromCsv(String(raw).trim()) || null)
                : null;
            const key = chegada || '__sem_horario__';
            if (!map.has(key)) {
                map.set(key, { arrivalTime: chegada, stops: [] });
            }
            map.get(key).stops.push(p);
        }
        return Array.from(map.values()).sort((a, b) => {
            if (a.arrivalTime === b.arrivalTime) return 0;
            if (a.arrivalTime == null) return 1;
            if (b.arrivalTime == null) return -1;
            return String(a.arrivalTime).localeCompare(String(b.arrivalTime));
        });
    },
    /**
     * Agrupa paradas pelo horario_saida (volta), com fallback de horário global quando aplicável.
     * Usado em "Ida e Volta" para rodar solveRouteGroup por faixa de saída.
     */
    groupStopsByDeparture(stops, { requireDeparture = false } = {}) {
        let formSaida = null;
        if (!requireDeparture) {
            for (const p of stops || []) {
                const raw = p && p.horario_saida;
                if (raw != null && String(raw).trim() !== '') {
                    const n = this.normalizeHHmmFromCsv(String(raw).trim());
                    if (n) {
                        formSaida = n;
                        break;
                    }
                }
            }
        }
        const map = new Map();
        for (const p of stops || []) {
            if (requireDeparture && (p == null || String(p.horario_saida || '').trim() === '')) {
                continue;
            }
            const raw = p && p.horario_saida;
            const norm = (raw != null && String(raw).trim() !== '')
                ? (this.normalizeHHmmFromCsv(String(raw).trim()) || null)
                : null;
            const saida = requireDeparture ? norm : (norm || formSaida);
            const key = saida || '__sem_horario__';
            if (!map.has(key)) {
                map.set(key, { horarioSaida: saida, stops: [] });
            }
            map.get(key).stops.push(p);
        }
        return Array.from(map.values()).sort((a, b) => {
            if (a.horarioSaida === b.horarioSaida) return 0;
            if (a.horarioSaida == null) return 1;
            if (b.horarioSaida == null) return -1;
            return String(a.horarioSaida).localeCompare(String(b.horarioSaida));
        });
    },
    getValidManualFleetEntries() {
        return (this.manualFleetEntries || [])
            .filter((entry) => entry.id_tipo_veiculo != null
                && isPositiveInt(entry.count)
                && isPositiveInt(entry.capacity))
            .map((entry) => {
                const typeId = Number(entry.id_tipo_veiculo);
                const row = this.vehicleTypeRows.find((r) => Number(r.id_tipo_veiculo) === typeId);
                return {
                    id_tipo_veiculo: typeId,
                    label: row ? row.descricao_tipo_veiculo : String(typeId),
                    count: parseInt(String(entry.count), 10),
                    capacity: parseInt(String(entry.capacity), 10),
                };
            });
    },
    mergeManualFleetEntries(entries) {
        const map = new Map();
        (entries || []).forEach((entry) => {
            const key = String(entry.id_tipo_veiculo);
            const existing = map.get(key);
            if (existing) {
                existing.count += entry.count;
                return;
            }
            map.set(key, { ...entry });
        });
        return Array.from(map.values());
    },
    resolveManualFleetPlan() {
        const entries = this.mergeManualFleetEntries(this.getValidManualFleetEntries());
        if (!entries.length) {
            return {
                numVeiculosUser: NaN,
                totalFleetCapacityGrupo: 0,
                entries: null,
            };
        }
        const numVeiculosUser = entries.reduce((sum, e) => sum + e.count, 0);
        const totalFleetCapacityGrupo = entries.reduce((sum, e) => sum + (e.count * e.capacity), 0);
        return {
            numVeiculosUser,
            totalFleetCapacityGrupo,
            entries,
        };
    },
    getManualFleetTypesForOptimizer(entries) {
        return (entries || []).map((entry) => ({
            key: String(entry.id_tipo_veiculo),
            id_tipo_veiculo: entry.id_tipo_veiculo,
            type: entry.label,
            capacity: entry.capacity,
            maxCount: entry.count,
            count: entry.count,
            profile: 'car',
        }));
    },
    _getManualFleetVehicleIdOffset() {
        const ids = Object.keys(this.fleetVehicleSpecById || {})
            .map((k) => Number(k))
            .filter((n) => Number.isFinite(n));
        if (!ids.length) return 0;
        return Math.max(...ids);
    },
    _offsetManualFleetVehicleIds(fleetPick, offset) {
        if (!offset || !fleetPick) return;
        const remapId = (id) => Number(id) + offset;
        const { vehicleSpecById, precomputedVersoBody, precomputedSolvePayload } = fleetPick;
        if (vehicleSpecById && typeof vehicleSpecById === 'object') {
            const nextSpec = {};
            Object.entries(vehicleSpecById).forEach(([id, spec]) => {
                nextSpec[remapId(id)] = spec;
            });
            Object.keys(vehicleSpecById).forEach((k) => { delete vehicleSpecById[k]; });
            Object.assign(vehicleSpecById, nextSpec);
        }
        if (precomputedVersoBody && Array.isArray(precomputedVersoBody.vehicles)) {
            precomputedVersoBody.vehicles.forEach((v) => {
                if (v && v.id != null) v.id = remapId(v.id);
            });
        }
        if (precomputedSolvePayload && Array.isArray(precomputedSolvePayload.routes)) {
            precomputedSolvePayload.routes.forEach((r) => {
                if (r && r.vehicle != null) r.vehicle = remapId(r.vehicle);
            });
        }
    },
    _consumeManualFleetRemaining(remaining, responseData, vehicleSpecById) {
        if (!Array.isArray(remaining) || !remaining.length) return;
        const usedByTypeKey = new Map();
        const usedIds = new Set();
        (responseData.routes || []).forEach((route) => {
            if (route._displacementLeg) return;
            if (route.vehicleId != null) usedIds.add(Number(route.vehicleId));
        });
        usedIds.forEach((id) => {
            const spec = vehicleSpecById[id];
            const typeKey = spec && spec.typeKey != null
                ? String(spec.typeKey)
                : String(spec && spec.type ? spec.type : '');
            if (!typeKey) return;
            usedByTypeKey.set(typeKey, (usedByTypeKey.get(typeKey) || 0) + 1);
        });
        remaining.forEach((entry) => {
            const key = String(entry.id_tipo_veiculo);
            const used = usedByTypeKey.get(key) || 0;
            entry.count = Math.max(0, Number(entry.count) - used);
        });
    },
    /** Endereço de uma linha do CSV de roteirização (preview ou stop materializado). */
    buildCsvLineAddress(line) {
        if (!line) return '';
        // Preferir campos estruturados: `endereco` pode vir como formattedAddress do geocode.
        const fromFields = this.buildAddressFromStop({
            logradouro: line.logradouro,
            numero: line.numero,
            bairro: line.bairro,
            cidade: line.cidade,
            estado: line.estado,
            cep: line.cep,
            id_pais: line.id_pais != null ? line.id_pais : this.resolveIdPais(line.pais),
        }, this.countries);
        if (fromFields) return fromFields;
        return String(line.endereco || '').trim();
    },
    /** True se o texto for só um par "lat, lng" (sem endereço legível). */
    isCoordOnlyAddress(value) {
        return /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(String(value || '').trim());
    },
    /** Endereço legível para marcador/tooltip original do CSV (após geocode). */
    resolveOriginalPointAddress(stop) {
        if (!stop) return '';
        const fromCsv = this.buildCsvLineAddress(stop);
        if (fromCsv && !this.isCoordOnlyAddress(fromCsv)) return fromCsv;
        const end = String(stop.endereco || '').trim();
        if (end && !this.isCoordOnlyAddress(end)) return end;
        const named = this.getStopMarkerName(stop);
        if (named && !this.isCoordOnlyAddress(named) && !/\(\s*-?\d/.test(named)) return named;
        return end || named || '';
    },
    /**
     * Escolhe raio_ideal / raio_maximo a partir das paradas-fonte de um cluster
     * (mínimo positivo dos membros; fallback radiusConfig).
     */
    pickRadiiFromStopGroup(stopGroup, servedAddresses) {
        const positive = (n) => Number.isFinite(Number(n)) && Number(n) > 0;
        const addrSet = new Set(
            (servedAddresses || []).map((a) => String(a || '').trim()).filter(Boolean)
        );
        const list = Array.isArray(stopGroup) ? stopGroup : [];
        const members = addrSet.size
            ? list.filter((s) => s && addrSet.has(String(s.endereco || '').trim()))
            : list;
        const source = members.length ? members : list;
        const ideals = source.map((s) => Number(s && s.raio_ideal)).filter(positive);
        const maxes = source.map((s) => Number(s && s.raio_maximo)).filter(positive);
        let raio_ideal = ideals.length ? Math.min(...ideals) : null;
        let raio_maximo = maxes.length ? Math.min(...maxes) : null;
        if (!positive(raio_ideal)) {
            const fallback = Number(this.radiusConfig && this.radiusConfig.ideal);
            raio_ideal = positive(fallback) ? fallback : null;
        }
        if (!positive(raio_maximo)) {
            const fallback = Number(this.radiusConfig && this.radiusConfig.max);
            raio_maximo = positive(fallback) ? fallback : null;
        }
        return { raio_ideal, raio_maximo };
    },
    /** Converte "HH:mm" (duração máxima de viagem) para segundos; usado no limite enviado ao Verso. */
    timeToSeconds(time) {
        const parts = String(time).trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
        if (!parts) return null;
        return (parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10)) * 60;
    },
    /**
     * Desloca um horário HH:mm por offsetSeconds.
     * direction = -1 → subtrai (ex.: horário de saída a partir do de chegada).
     * direction = +1 → soma (ex.: horário de chegada a partir do de saída).
     */
    offsetTime(timeHHmm, offsetSeconds, direction) {
        const parts = String(timeHHmm).trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
        if (!parts) return null;
        const now = new Date();
        const base = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parseInt(parts[1], 10), parseInt(parts[2], 10), 0, 0);
        const result = new Date(base.getTime() + direction * offsetSeconds * 1000);
        return `${String(result.getHours()).padStart(2, '0')}:${String(result.getMinutes()).padStart(2, '0')}`;
    },
    /**
     * Passo de visita na rota Verso/VROOM (type pode variar em caixa ou vir sem type).
     */
     isJobStep(step) {
        if (!step) return false;
        const t = String(step.type || '').toLowerCase();
        if (t === 'start' || t === 'end' || t === 'break') return false;
        if (t === 'job' || t === 'pickup' || t === 'delivery') return true;
        if (step.id != null && step.id !== '' && Array.isArray(step.location) && step.location.length >= 2) {
            return true;
        }
        return false;
    },
    /**
     * Duração (s) da rota principal para validação de tempo máximo de viagem.
     * Desconta pernas de deslocamento garagem→1ª parada (IDA) ou última parada→garagem (VOLTA).
     */
    getRouteDuration(vr, opts = {}) {
        const {
            hasStartBase = null,
            hasEndBase = false,
            isVolta = false,
            skipGarageDepartureVehicleIds = null,
            garageReturnVehicleIds = null,
            repositionStartVehicleIds = null,
            terminalServiceSec = 0,
        } = opts;
        const travelSec = Number(vr && vr.duration || 0);
        const serviceSec = Number(vr && vr.service || 0);
        // Serviço do terminal (CD): não é step nenhum, mas conta no tempo da viagem.
        const terminalSec = Number(terminalServiceSec) || 0;
        const full = travelSec + serviceSec + terminalSec;
        const steps = (vr && vr.steps) || [];
        const jobSteps = steps.filter((s) => this.isJobStep(s));
        if (!jobSteps.length) return null;

        const stepType = (step) => String(step && step.type || '').toLowerCase();
        const vehicleId = vr.vehicle != null ? Number(vr.vehicle) : null;
        const skipDeparture = new Set(
            (skipGarageDepartureVehicleIds || []).map(Number).filter(Number.isFinite),
        );
        const garageReturn = new Set(
            (garageReturnVehicleIds || []).map(Number).filter(Number.isFinite),
        );
        const hasExplicitSkipDeparture = Array.isArray(skipGarageDepartureVehicleIds);
        const hasExplicitGarageReturn = Array.isArray(garageReturnVehicleIds);
        const effectiveHasStartBase = hasStartBase != null
            ? hasStartBase
            : (this.initialBaseStop == 1);
        const routeHasEndBase = hasExplicitGarageReturn && vehicleId != null
            ? garageReturn.has(vehicleId)
            : !!hasEndBase;
        // Reaproveitado que parte do fim da rota anterior: o trecho até a 1ª parada é
        // deslocamento, como a perna de garagem, e fica fora do tempo máximo de viagem.
        const repositionStart = new Set(
            (repositionStartVehicleIds || []).map(Number).filter(Number.isFinite),
        );
        const routeHasStartBase = vehicleId != null && repositionStart.has(vehicleId)
            ? true
            : (hasExplicitSkipDeparture && vehicleId != null
                ? (!!effectiveHasStartBase && !skipDeparture.has(vehicleId))
                : !!effectiveHasStartBase);

        const idaDisplacementTravelSec = () => {
            const firstJob = jobSteps[0];
            const startStep = steps.find((s) => stepType(s) === 'start');
            const firstJobIdx = steps.findIndex((s) => s === firstJob);
            const startArr = Number(startStep && startStep.arrival) || 0;
            const j0Arr = Number(firstJob.arrival);
            if (Number.isFinite(j0Arr) && j0Arr > startArr) return j0Arr - startArr;
            if (startStep && firstJobIdx > 0) {
                let travel = 0;
                for (let i = 0; i < firstJobIdx; i++) {
                    if (stepType(steps[i]) === 'start') continue;
                    travel += Number(steps[i].duration) || 0;
                }
                if (travel > 0) return travel;
            }
            const j0Dur = Number(firstJob.duration);
            const startDur = Number(startStep && startStep.duration) || 0;
            if (Number.isFinite(j0Dur) && j0Dur > startDur) return j0Dur - startDur;
            return 0;
        };

        const voltaDisplacementTravelSec = () => {
            const lastJob = jobSteps[jobSteps.length - 1];
            const endStep = steps.find((s) => stepType(s) === 'end');
            if (!lastJob || !endStep) return 0;
            const jNArr = Number(lastJob.arrival);
            const endArr = Number(endStep.arrival);
            if (Number.isFinite(jNArr) && Number.isFinite(endArr) && endArr > jNArr) return endArr - jNArr;
            const lastJobIdx = steps.findIndex((s) => s === lastJob);
            const endIdx = steps.findIndex((s) => s === endStep);
            if (lastJobIdx >= 0 && endIdx > lastJobIdx) {
                let travel = 0;
                for (let i = lastJobIdx + 1; i <= endIdx; i++) {
                    travel += Number(steps[i].duration) || 0;
                }
                if (travel > 0) return travel;
            }
            return 0;
        };

        if (isVolta && routeHasEndBase) {
            const startStep = steps.find((s) => stepType(s) === 'start');
            const lastJob = jobSteps[jobSteps.length - 1];
            const jNArr = Number(lastJob.arrival);
            const startArr = Number(startStep && startStep.arrival);
            if (Number.isFinite(jNArr) && Number.isFinite(startArr)) {
                const mainSec = Math.max(0, jNArr - startArr);
                if (mainSec > 0) return mainSec + terminalSec;
            }
            const dispTravel = voltaDisplacementTravelSec();
            if (dispTravel > 0 && dispTravel < travelSec) {
                return Math.max(0, travelSec - dispTravel) + serviceSec + terminalSec;
            }
            return full;
        }

        if (!isVolta && routeHasStartBase) {
            const endStep = steps.find((s) => stepType(s) === 'end');
            const startStep = steps.find((s) => stepType(s) === 'start');
            const firstJob = jobSteps[0];
            const j0Arr = Number(firstJob.arrival);
            const endArr = Number(endStep && endStep.arrival);
            const startArr = Number(startStep && startStep.arrival) || 0;
            if (Number.isFinite(j0Arr) && Number.isFinite(endArr) && j0Arr > startArr) {
                const mainSec = Math.max(0, endArr - j0Arr);
                if (mainSec > 0) return mainSec + terminalSec;
            }
            const dispTravel = idaDisplacementTravelSec();
            if (dispTravel > 0 && dispTravel < travelSec) {
                return Math.max(0, travelSec - dispTravel) + serviceSec + terminalSec;
            }
            return full;
        }

        return full;
    },
    /** Ids dos reaproveitados que partem do ponto final da rota anterior (`vehicleStartById`). */
    _repositionStartVehicleIds(reuseFrom) {
        if (!reuseFrom || !reuseFrom.vehicleStartById) return null;
        return Object.keys(reuseFrom.vehicleStartById).map(Number).filter(Number.isFinite);
    },
    /** Distância (m) e tempo (s) do início do veículo até a 1ª parada (steps cumulativos do VROOM). */
    _repositionMetrics(startStep, firstJobStep) {
        if (!firstJobStep) return { distance: 0, durationSec: 0 };
        const startDist = Number(startStep && startStep.distance) || 0;
        const startArr = Number(startStep && startStep.arrival) || 0;
        const dist = Number(firstJobStep.distance);
        const arr = Number(firstJobStep.arrival);
        return {
            distance: Number.isFinite(dist) ? Math.max(0, dist - startDist) : 0,
            durationSec: Number.isFinite(arr) ? Math.max(0, arr - startArr) : 0,
        };
    },
    /**
     * Normaliza a resposta Verso/VROOM para o modelo interno da tela (campos planos: geometry, distance, durationSec).
     */
    normalizeVersoRoutes(versoData, stops, numVehiclesPlanned, hasStartBaseOverride = null, sentidoExtensoOverride = null, hasEndBaseOverride = false, displacementFlags = null) {
        const rawRoutes = versoData.routes;
        const routes = rawRoutes.filter(r => (r.steps || []).some(s => this.isJobStep(s)));
        const skipDeparture = new Set(
            ((displacementFlags && displacementFlags.skipGarageDepartureVehicleIds) || [])
                .map(Number)
                .filter(Number.isFinite),
        );
        const garageReturn = new Set(
            ((displacementFlags && displacementFlags.garageReturnVehicleIds) || [])
                .map(Number)
                .filter(Number.isFinite),
        );
        const hasExplicitSkipDeparture = displacementFlags
            && Array.isArray(displacementFlags.skipGarageDepartureVehicleIds);
        const hasExplicitGarageReturn = displacementFlags
            && Array.isArray(displacementFlags.garageReturnVehicleIds);
        // Reaproveitados que partem do ponto final da rota anterior: o trecho até a 1ª parada é
        // deslocamento (fora do custo; conta só como hora do motorista).
        const repositionStart = new Set(
            ((displacementFlags && displacementFlags.repositionStartVehicleIds) || [])
                .map(Number)
                .filter(Number.isFinite),
        );
        const physicalIds = new Set(
            routes.map((r) => {
                const vId = r.vehicle != null ? Number(r.vehicle) : null;
                return vId;
            }).filter((id) => id != null)
        );
        const vehiclesUsed = physicalIds.size || routes.length;
        const summary = {
            vehiclesUsed,
            totalVehicles: numVehiclesPlanned,
            totalStops: stops.length,
            clusterType: this.clusterType,
        };
        return {
            summary,
            routes: routes.map((vr, routeIndex) => {
                const allSteps = vr.steps || [];
                const startStep = allSteps.find(s => String(s.type || '').toLowerCase() === 'start');
                const endStep = allSteps.find(s => String(s.type || '').toLowerCase() === 'end');
                const jobSteps = allSteps.filter(s => this.isJobStep(s));
                const vehicleStops = jobSteps.map((step, i) => {
                    const idx = step.id != null ? Number(step.id) - 1 : -1;
                    const base = idx >= 0 && idx < stops.length ? stops[idx] : null;
                    return {
                        eta: step.arrival != null ? Number(step.arrival) : null,
                        lat: base ? base.lat : (step.location && step.location[1]),
                        lng: base ? base.lng : (step.location && step.location[0]),
                        passengerAmount: base ? base.passengerAmount : 1,
                        shipmentLabel: base && base.name ? base.name : this.$t('routePlanning.fields.stopNumber', { n: (idx >= 0 ? idx : i) + 1 }),
                        visitIndex: i,
                        // Vínculo estável job→parada (evita .find por coord pegar o 1º cluster/rota).
                        ...(base && Array.isArray(base.servedAddresses) && base.servedAddresses.length
                            ? { servedAddresses: base.servedAddresses.slice() }
                            : {}),
                    };
                });
                const dist = vr.distance;
                const dur = Number(vr.duration) + Number(vr.service || 0);
                // Garante ETA em cada parada (segundos desde o início do veículo).
                if (vehicleStops.length === 1) {
                    if (!Number.isFinite(Number(vehicleStops[0].eta))) vehicleStops[0].eta = 0;
                } else if (vehicleStops.length > 1) {
                    vehicleStops.forEach((s, i) => {
                        if (!Number.isFinite(Number(s.eta))) {
                            s.eta = Math.round((dur * i) / (vehicleStops.length - 1));
                        }
                    });
                }
                const geometry = vr.geometry || null;
                const vehicleId = vr.vehicle != null ? Number(vr.vehicle) : null;
                let routeHasStartbase = hasStartBaseOverride != null
                    ? hasStartBaseOverride
                    : (this.initialBaseStop == 1);
                let routeHasEndbase = !!hasEndBaseOverride;
                if (hasExplicitSkipDeparture && vehicleId != null && skipDeparture.has(vehicleId)) {
                    routeHasStartbase = false;
                }
                if (hasExplicitGarageReturn && vehicleId != null) {
                    routeHasEndbase = garageReturn.has(vehicleId);
                }
                const reposition = !routeHasStartbase && vehicleId != null && repositionStart.has(vehicleId)
                    ? this._repositionMetrics(startStep, jobSteps[0])
                    : null;
                return {
                    routeIndex: routeIndex,
                    vehicleId,
                    stops: vehicleStops,
                    hasStartbase: routeHasStartbase,
                    hasEndbase: routeHasEndbase,
                    startArrivalSec: startStep && startStep.arrival != null ? Number(startStep.arrival) : null,
                    endArrivalSec: endStep && endStep.arrival != null ? Number(endStep.arrival) : null,
                    geometry,
                    distance: dist,
                    durationSec: dur,
                    direction: sentidoExtensoOverride,
                    versoRoute: vr,
                    ...(reposition ? {
                        repositionDistance: reposition.distance,
                        repositionDurationSec: reposition.durationSec,
                    } : {}),
                };
            })
        };
    },
    /**
     * Aplica o JSON do worker (results/*.out.json) após status DONE no scheduler.
     */
    async applyCluster(resultado, stopGroup, arrivalTimeGrupo, horarioSaidaGrupo = null, preservedPcdStops = []) {
        let currentStops = [...stopGroup];
        let clusterTypeLocal = null;
        const data = resultado || {};
        const pcdStops = Array.isArray(preservedPcdStops) ? preservedPcdStops : [];
        if (data.noop) {
            if (pcdStops.length) {
                currentStops = [...currentStops, ...pcdStops];
            }
            return { currentStops, clusterTypeLocal };
        }
        if (data.error) {
            // Sem medição da caminhada real o worker não devolve resultado "aprovado" em silêncio.
            const semMedicao = data.messageParams && data.messageParams.reason === 'walk_unavailable';
            this.showError({
                message: this.$t(semMedicao
                    ? 'routePlanning.validation.clusterizacaoCaminhadaIndisponivel'
                    : 'routePlanning.validation.clusterizacaoFalhou'),
            });
            return { error: true };
        }
        const clusters = data.clusters;
        // walkValidation.verificado=false: a caminhada real não foi medida (Fase B desligada ou
        // política shrink sem medição). A linha reta continua garantida pelo worker.
        this.clusterWalkValidation = data.walkValidation || null;
        if (data.walkValidation && data.walkValidation.verificado === false) {
            console.warn('clusterização: caminhada real NÃO verificada', data.walkValidation);
        }
        const clusterTypeGrupo = data.clusterTypeKey;
        if (clusters && clusters.length < currentStops.length) {
            clusterTypeLocal = clusterTypeGrupo;
            this.clusterType = clusterTypeGrupo;
            // Defesa em profundidade: o que o worker devolve precisa conservar os passageiros e ter
            // coordenada válida em todo cluster. Descartar cluster em silêncio fazia passageiro sumir.
            const paxDe = (v) => Math.trunc(Number(v)) || 1;
            const paxEntrada = currentStops.reduce((s, p) => s + paxDe(p && p.passengerAmount), 0);
            const paxSaida = clusters.reduce((s, c) => s + paxDe(c && c.passengerAmount), 0);
            const coordInvalida = clusters.some(
                (c) => !Number.isFinite(Number(c && c.lat)) || !Number.isFinite(Number(c && c.lng)),
            );
            if (coordInvalida || paxEntrada !== paxSaida) {
                console.error('clusterização: resultado inconsistente', { coordInvalida, paxEntrada, paxSaida });
                this.showError({ message: this.$t('routePlanning.validation.clusterizacaoFalhou') });
                return { error: true };
            }
            currentStops = [];
            for (const cluster of clusters) {
                const lat = Number(cluster.lat);
                const lng = Number(cluster.lng);
                // Sem geocodificação reversa aqui: o worker devolve `endereco: null` em
                // todo cluster regular, então isto era UMA chamada HTTP por cluster, em
                // série — centenas delas queimavam a cota da API e dominavam o tempo do
                // fluxo. O endereço legível vem depois, sob demanda, pelo botão de
                // geocodificar paradas (reverseGeocodePendingStops), que resolve tudo em
                // lotes. Endereço "lat, lng" é estado tolerado: `isCoordOnlyAddress` o
                // reconhece e a prévia de área já o usa de propósito.
                const preservedAddress = cluster.endereco != null && String(cluster.endereco).trim();
                const formattedAddress = preservedAddress || `${lat}, ${lng}`;
                currentStops.push({
                    endereco: formattedAddress,
                    latitude: lat,
                    longitude: lng,
                    id_pais: null,
                    cep: null,
                    logradouro: null,
                    numero: null,
                    estado: null,
                    cidade: null,
                    bairro: null,
                    passengerAmount: cluster.passengerAmount,
                    pcd: cluster.hasPcd ? 1 : 0,
                    servedAddresses: Array.isArray(cluster.servedAddresses)
                        ? cluster.servedAddresses.slice()
                        : [],
                    horario_chegada: cluster.horario_chegada
                        || arrivalTimeGrupo
                        || this.pickScheduleFieldFromStopGroup(stopGroup, 'horario_chegada')
                        || '',
                    horario_saida: cluster.horario_saida
                        || horarioSaidaGrupo
                        || this.pickScheduleFieldFromStopGroup(stopGroup, 'horario_saida')
                        || '',
                    ...this.pickRadiiFromStopGroup(
                        stopGroup,
                        Array.isArray(cluster.servedAddresses) ? cluster.servedAddresses : [],
                    ),
                });
            }
            this.applyTurnosToStops(currentStops);
        }
        if (pcdStops.length) {
            currentStops = [...currentStops, ...pcdStops];
        }
        return { currentStops, clusterTypeLocal };
    },
    /** Filtra paradas com coordenadas e monta o formato mínimo (lat, lng, passageiros, nome) para o solver. */
    mapStops(currentStops) {
        return currentStops
            .filter(p => p.latitude && p.longitude)
            .map(p => {
                const pa = Number(p.passengerAmount);
                return {
                    lat: p.latitude,
                    lng: p.longitude,
                    passengerAmount: pa != null && pa >= 1 ? pa : 1,
                    name: this.getStopMarkerName(p),
                    ...(p.id_turno != null ? { id_turno: p.id_turno } : {}),
                    ...(Array.isArray(p.servedAddresses) && p.servedAddresses.length
                        ? { servedAddresses: p.servedAddresses.slice() }
                        : {}),
                };
            });
    },
    _timeToMinutes(hhmm) {
        if (hhmm == null || String(hhmm).trim() === '') return null;
        const parts = String(hhmm).trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
        if (!parts) return null;
        return parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
    },
    countPassengerTurnosFromStops(stops) {
        const ids = new Set();
        (stops || []).forEach((s) => {
            if (s && s.id_turno != null && Number.isFinite(Number(s.id_turno))) {
                ids.add(Number(s.id_turno));
            }
        });
        return Math.max(1, ids.size || 1);
    },
    _reuseCoordKey(from, to) {
        const f = `${Number(from.lat).toFixed(6)},${Number(from.lng).toFixed(6)}`;
        const t = `${Number(to.lat).toFixed(6)},${Number(to.lng).toFixed(6)}`;
        return `${f}|${t}`;
    },
    async _getRepositionDurationSec(fromPoint, toPoint, repositionCache) {
        if (!fromPoint || !toPoint) return null;
        const cache = repositionCache || new Map();
        const key = this._reuseCoordKey(fromPoint, toPoint);
        if (cache.has(key)) return cache.get(key);
        const start = [Number(fromPoint.lng), Number(fromPoint.lat)];
        const end = [Number(toPoint.lng), Number(toPoint.lat)];
        const jobs = [
            { id: 1, location: end, service: 0 },
        ];
        const vehicle = {
            id: 1,
            profile: 'car',
            start,
            end,
            steps: [
                { type: 'start' },
                { type: 'job', id: 1 },
                { type: 'end' },
            ],
        };
        try {
            const planRes = await this._requestVersoPlanScheduled(
                { jobs, vehicles: [vehicle] },
                { waitMs: VERSO_PLAN_AUX_WAIT_MS },
            );
            const planned = planRes.data;
            if (!planned || planned.code !== 0 || !Array.isArray(planned.routes) || !planned.routes.length) {
                cache.set(key, null);
                return null;
            }
            const route = planned.routes[0];
            const dur = Number(route.duration);
            const sec = Number.isFinite(dur) && dur >= 0 ? dur : null;
            cache.set(key, sec);
            return sec;
        } catch (e) {
            cache.set(key, null);
            return null;
        }
    },
    /**
     * Preenche o cache de reposição de vários pares (from → to) com UM POST /verso/plan por
     * lote (um veículo e um job por par), em vez de um POST sequencial por veículo — cada POST
     * passa pelo espaçamento global do Vroom, então centenas deles somavam minutos de espera.
     * Par que falha entra no cache como null (mesma regra de _getRepositionDurationSec).
     */
    async _prefetchRepositionDurations(pairs, repositionCache) {
        if (!repositionCache || !Array.isArray(pairs) || !pairs.length) return;
        const seen = new Set();
        const todo = [];
        for (const { from, to } of pairs) {
            if (!from || !to) continue;
            const key = this._reuseCoordKey(from, to);
            if (repositionCache.has(key) || seen.has(key)) continue;
            seen.add(key);
            todo.push({ key, from, to });
        }
        if (todo.length < 2) return; // 0/1 par: o caminho unitário já resolve
        const CHUNK = 100;
        const lotes = [];
        for (let i = 0; i < todo.length; i += CHUNK) lotes.push(todo.slice(i, i + CHUNK));
        await Promise.all(lotes.map(async (lote) => {
            const jobs = [];
            const vehicles = [];
            lote.forEach((par, idx) => {
                const id = idx + 1;
                const start = [Number(par.from.lng), Number(par.from.lat)];
                const end = [Number(par.to.lng), Number(par.to.lat)];
                jobs.push({ id, location: end, service: 0 });
                vehicles.push({
                    id,
                    profile: 'car',
                    start,
                    end,
                    steps: [{ type: 'start' }, { type: 'job', id }, { type: 'end' }],
                });
            });
            const durById = new Map();
            try {
                const planRes = await this._requestVersoPlanScheduled(
                    { jobs, vehicles },
                    { waitMs: VERSO_PLAN_AUX_WAIT_MS },
                );
                const planned = planRes.data;
                if (planned && planned.code === 0 && Array.isArray(planned.routes)) {
                    planned.routes.forEach((route) => {
                        const dur = Number(route.duration);
                        if (Number.isFinite(dur) && dur >= 0) durById.set(Number(route.vehicle), dur);
                    });
                }
            } catch (e) {
                // pares do lote ficam null abaixo
            }
            lote.forEach((par, idx) => {
                repositionCache.set(par.key, durById.has(idx + 1) ? durById.get(idx + 1) : null);
            });
        }));
    },
    _firstIdaStopFromScheduleItem(item) {
        if (!item || item.type !== 'ida' || !Array.isArray(item.stops) || !item.stops.length) {
            return null;
        }
        const mapped = this.mapStops(item.stops);
        if (!mapped.length) return null;
        return { lat: Number(mapped[0].lat), lng: Number(mapped[0].lng) };
    },
    _isSameReuseLocation(a, b) {
        if (!a || !b) return false;
        const d = haversineMeters(a.lat, a.lng, b.lat, b.lng);
        return Number.isFinite(d) && d <= REUSE_SAME_LOCATION_M;
    },
    /**
     * O veículo (estado da última rota que fez) pode fazer a próxima rota, de qualquer sentido?
     * - fim da anterior e início da próxima no mesmo local: fim + tempo mínimo de transferência;
     * - locais diferentes: fim + deslocamento (VROOM) do ponto final até o ponto inicial.
     * Na volta o veículo precisa estar no CD antes do embarque (estimado pelo contingente da
     * rota anterior).
     */
    async _canReuseVehicleForLeg(state, { nextLeg, nextDeparture, nextStartPoint }, repositionCache) {
        if (!state || !state.routeEndTime) return false;
        const endMin = this._timeToMinutes(state.routeEndTime);
        const depMin = this._timeToMinutes(nextDeparture);
        if (endMin == null || depMin == null) return false;
        let readySec = depMin * 60;
        if (nextLeg === 'volta') {
            readySec -= Number(jobServiceSeconds(state.passageirosUltimaPerna, this.stopServiceConfig)) || 0;
        }
        let transferSec;
        if (!state.endPoint || !nextStartPoint || this._isSameReuseLocation(state.endPoint, nextStartPoint)) {
            transferSec = Math.max(0, Number(this.tempoReusoVeiculos) || 0) * 60;
        } else {
            const repositionSec = await this._getRepositionDurationSec(
                state.endPoint,
                nextStartPoint,
                repositionCache,
            );
            if (repositionSec == null) return false;
            transferSec = repositionSec;
        }
        return readySec >= endMin * 60 + transferSec;
    },
    /** Próxima rota de um item da fila de horários: `{ nextLeg, nextDeparture, nextStartPoint }`. */
    _reuseTargetFromScheduleItem(item, maxTripSec, cdPoint) {
        if (!item) return null;
        if (item.type === 'volta') {
            return item.horarioSaida
                ? { nextLeg: 'volta', nextDeparture: item.horarioSaida, nextStartPoint: cdPoint || null }
                : null;
        }
        const firstIdaStop = this._firstIdaStopFromScheduleItem(item);
        if (!firstIdaStop || !item.arrivalTime) return null;
        return {
            nextLeg: 'ida',
            nextDeparture: this.offsetTime(item.arrivalTime, maxTripSec, -1),
            nextStartPoint: firstIdaStop,
        };
    },
    async _vehicleMayReuseOnFutureItems(state, futureItems, maxTripSec, cdPoint, repositionCache) {
        if (!state || !Array.isArray(futureItems) || !futureItems.length) return false;
        for (const item of futureItems) {
            const target = this._reuseTargetFromScheduleItem(item, maxTripSec, cdPoint);
            if (!target) continue;
            if (await this._canReuseVehicleForLeg(state, target, repositionCache)) return true;
        }
        return false;
    },
    _computeReuseDeparturesScratch(responseData, ctx) {
        const { arrivalTimeGrupo, horarioSaidaGrupo } = ctx;
        const departuresScratch = [];
        (responseData.routes || []).forEach((route, index) => {
            const rawDuration = route._startbaseFullVehicleDurationSec != null
                ? route._startbaseFullVehicleDurationSec
                : route.durationSec;
            const totalDurationSeconds = parseDurationSeconds(rawDuration);
            if (!arrivalTimeGrupo && !horarioSaidaGrupo) {
                departuresScratch[index] = null;
                return;
            }
            const fallbackDeparture = horarioSaidaGrupo
                ? horarioSaidaGrupo
                : this.offsetTime(arrivalTimeGrupo, totalDurationSeconds, -1);
            let departureTime;
            if (route._startbaseMainContinuation && index > 0) {
                const prevR = responseData.routes[index - 1];
                const prevDep = departuresScratch[index - 1];
                const prevEtaSec = Number(prevR && prevR.endArrivalSec);
                if (prevR && prevR._displacementLeg && prevDep != null && prevEtaSec != null) {
                    departureTime = this.offsetTime(prevDep, prevEtaSec, 1);
                } else {
                    departureTime = fallbackDeparture;
                }
            } else if (route._endbaseDisplacementLeg && index > 0) {
                const prevR = responseData.routes[index - 1];
                const prevDep = departuresScratch[index - 1];
                const prevEndSec = Number(prevR && prevR.endArrivalSec);
                if (prevDep != null && prevEndSec != null) {
                    departureTime = this.offsetTime(prevDep, prevEndSec, 1);
                } else {
                    departureTime = fallbackDeparture;
                }
            } else {
                departureTime = fallbackDeparture;
            }
            departuresScratch[index] = departureTime;
        });
        return departuresScratch;
    },
    _extractRouteEndPoint(route, ctx) {
        const { isVolta, hasEndBase, idaStartBase } = ctx;
        if (route._endbaseDisplacementLeg && idaStartBase) {
            return { lat: idaStartBase[1], lng: idaStartBase[0] };
        }
        const stops = route.stops || [];
        const lastS = stops[stops.length - 1];
        if (lastS && lastS.lat != null && lastS.lng != null) {
            return { lat: Number(lastS.lat), lng: Number(lastS.lng) };
        }
        if (isVolta && hasEndBase && idaStartBase) {
            return { lat: idaStartBase[1], lng: idaStartBase[0] };
        }
        return null;
    },
    _buildVehicleReuseState(responseData, ctx) {
        const { isVolta, arrivalTimeGrupo } = ctx;
        const departuresScratch = this._computeReuseDeparturesScratch(responseData, ctx);
        const routes = responseData.routes || [];
        const byVehicle = new Map();
        routes.forEach((route, index) => {
            if (route._displacementLeg && !route._endbaseDisplacementLeg) return;
            const vId = route.vehicleId != null ? Number(route.vehicleId) : null;
            if (vId == null) return;
            if (!byVehicle.has(vId)) byVehicle.set(vId, []);
            byVehicle.get(vId).push({ route, index });
        });
        const out = {};
        byVehicle.forEach((entries, vId) => {
            const mainEntry = entries.find((e) => !e.route._displacementLeg) || entries[0];
            const lastEntry = entries[entries.length - 1];
            const paradasDaPerna = (mainEntry && mainEntry.route && mainEntry.route.stops) || [];
            const terminalSec = this.terminalServiceSecForStops(paradasDaPerna, isVolta);
            let routeEndTime = null;
            if (!isVolta && arrivalTimeGrupo && mainEntry && !mainEntry.route._displacementLeg) {
                // O horário de chegada é o do portão; o desembarque acontece depois dele.
                routeEndTime = this.offsetTime(arrivalTimeGrupo, terminalSec, 1);
            } else {
                const dep = departuresScratch[lastEntry.index];
                const rawDuration = lastEntry.route._startbaseFullVehicleDurationSec != null
                    ? lastEntry.route._startbaseFullVehicleDurationSec
                    : lastEntry.route.durationSec;
                const segSec = parseDurationSeconds(rawDuration);
                if (dep != null && Number.isFinite(segSec)) {
                    routeEndTime = this.offsetTime(dep, segSec + terminalSec, 1);
                }
            }
            if (!routeEndTime) return;
            // Com recolhe à garagem, a última perna é o deslocamento: fim e ponto final são os
            // da garagem, e o reuso seguinte paga o deslocamento de lá.
            const hasGarageReturn = entries.some((e) => e.route._endbaseDisplacementLeg);
            const endPoint = this._extractRouteEndPoint(lastEntry.route, {
                ...ctx,
                hasEndBase: hasGarageReturn,
            });
            out[vId] = {
                routeEndTime,
                lastLeg: isVolta ? 'volta' : 'ida',
                endPoint,
                // Mesmo contingente costuma voltar: serve de estimativa do embarque no CD.
                passageirosUltimaPerna: paradasDaPerna.reduce(
                    (soma, s) => soma + Math.max(1, parseInt(String(s && s.passengerAmount), 10) || 1),
                    0,
                ),
            };
        });
        return out;
    },
    createVehicleReusePool() {
        const vm = this;
        return {
            vehicles: new Map(),
            spec: {},
            vehicleState: new Map(),
            repositionCache: new Map(),
            maxVehicleId: 0,
            registerFromSolve(pickedFleet, vehicleSpecById, reuseStateByVehicle, legCtx = {}) {
                const payload = pickedFleet && pickedFleet.precomputedSolvePayload;
                const versoBody = pickedFleet && pickedFleet.precomputedVersoBody;
                if (!payload || !versoBody) return;
                const legIsVolta = !!legCtx.isVolta;
                const legType = legIsVolta ? 'volta' : 'ida';
                const isJob = (step) => {
                    if (!step) return false;
                    const t = String(step.type || '').toLowerCase();
                    if (t === 'start' || t === 'end' || t === 'break') return false;
                    if (t === 'job' || t === 'pickup' || t === 'delivery') return true;
                    return step.id != null && step.id !== '' && Array.isArray(step.location) && step.location.length >= 2;
                };
                const usedIds = new Set();
                (payload.routes || []).forEach((route) => {
                    const vId = route.vehicle != null ? Number(route.vehicle) : NaN;
                    if (!Number.isFinite(vId)) return;
                    if (!(route.steps || []).some((s) => isJob(s))) return;
                    usedIds.add(vId);
                });
                const byId = new Map();
                (versoBody.vehicles || []).forEach((v) => {
                    if (v && v.id != null) byId.set(Number(v.id), v);
                });
                usedIds.forEach((id) => {
                    const v = byId.get(id);
                    const spec = vehicleSpecById && vehicleSpecById[id];
                    if (v) this.vehicles.set(id, v);
                    if (spec) this.spec[id] = { ...spec, physicalVehicleId: id };
                    this.maxVehicleId = Math.max(this.maxVehicleId, id);
                    const built = reuseStateByVehicle && reuseStateByVehicle[id];
                    const state = built
                        ? { ...built, lastLeg: legType }
                        : { lastLeg: legType, routeEndTime: null, endPoint: null };
                    this.vehicleState.set(id, state);
                });
            },
            /**
             * Volta com garagem: quem pode ser reaproveitado depois desta volta (qualquer rota
             * futura, ida ou volta) não recolhe à garagem.
             */
            async computeGarageReturnFlags({ futureItems, maxTripSec, voltaDeparture, voltaStops, cdPoint }) {
                const garageReturnVehicleIds = [];
                const skipGarageReturnVehicleIds = [];
                const mappedVolta = vm.mapStops(voltaStops || []);
                const lastVoltaStop = mappedVolta.length
                    ? {
                        lat: Number(mappedVolta[mappedVolta.length - 1].lat),
                        lng: Number(mappedVolta[mappedVolta.length - 1].lng),
                    }
                    : null;
                const targets = (futureItems || [])
                    .map((item) => vm._reuseTargetFromScheduleItem(item, maxTripSec, cdPoint))
                    .filter(Boolean);
                // Todos os deslocamentos candidatos (fim previsto da volta → início de cada rota
                // futura) num lote só.
                const prefetchPairs = [];
                for (const [id] of this.vehicles.entries()) {
                    const state = this.vehicleState.get(id);
                    const from = lastVoltaStop || (state && state.endPoint);
                    if (!from) continue;
                    targets.forEach((t) => {
                        if (t.nextStartPoint && !vm._isSameReuseLocation(from, t.nextStartPoint)) {
                            prefetchPairs.push({ from, to: t.nextStartPoint });
                        }
                    });
                }
                await vm._prefetchRepositionDurations(prefetchPairs, this.repositionCache);
                for (const [id] of this.vehicles.entries()) {
                    const state = this.vehicleState.get(id);
                    if (!state) continue;
                    const predictedVoltaState = {
                        lastLeg: 'volta',
                        routeEndTime: voltaDeparture
                            ? vm.offsetTime(voltaDeparture, maxTripSec, 1)
                            : state.routeEndTime,
                        endPoint: lastVoltaStop || state.endPoint,
                        passageirosUltimaPerna: state.passageirosUltimaPerna,
                    };
                    const mayReuse = await vm._vehicleMayReuseOnFutureItems(
                        predictedVoltaState,
                        futureItems,
                        maxTripSec,
                        cdPoint,
                        this.repositionCache,
                    );
                    if (mayReuse) {
                        skipGarageReturnVehicleIds.push(id);
                    } else {
                        garageReturnVehicleIds.push(id);
                    }
                }
                return { garageReturnVehicleIds, skipGarageReturnVehicleIds };
            },
            /**
             * Veículos do pool que podem fazer a próxima rota (ida ou volta), sem exigir alternância
             * de sentido: mesmo local → tempo mínimo de transferência; senão, o deslocamento.
             * Na ida com base inicial, o reaproveitado parte do ponto final da rota anterior
             * (vehicleStartById); sem base inicial a rota começa na 1ª parada.
             */
            async getReuseFrom({ nextLeg, nextDeparture, nextStartPoint }) {
                if (nextLeg !== 'ida' && nextLeg !== 'volta') return null;
                const pairs = [];
                for (const [id] of this.vehicles.entries()) {
                    const st = this.vehicleState.get(id);
                    if (!st || !st.endPoint || !nextStartPoint) continue;
                    if (vm._isSameReuseLocation(st.endPoint, nextStartPoint)) continue;
                    pairs.push({ from: st.endPoint, to: nextStartPoint });
                }
                await vm._prefetchRepositionDurations(pairs, this.repositionCache);
                const vehicles = [];
                const specById = {};
                const vehicleStartById = {};
                const vehicleRepositionSecById = {};
                for (const [id, v] of this.vehicles.entries()) {
                    const state = this.vehicleState.get(id);
                    if (!state) continue;
                    const eligible = await vm._canReuseVehicleForLeg(
                        state,
                        { nextLeg, nextDeparture, nextStartPoint },
                        this.repositionCache,
                    );
                    if (!eligible) continue;
                    // Sem base inicial não há trecho de deslocamento: a ida começa na 1ª parada
                    // (a volta, no destino), em vez de partir do fim da rota anterior do veículo.
                    if (nextLeg === 'ida' && state.endPoint && vm.initialBaseStop == 1) {
                        vehicleStartById[id] = [state.endPoint.lng, state.endPoint.lat];
                        // O solve soma este deslocamento ao max_travel_time do veículo: ele não
                        // conta no tempo máximo de viagem dos passageiros.
                        const key = nextStartPoint ? vm._reuseCoordKey(state.endPoint, nextStartPoint) : null;
                        const sec = key && this.repositionCache.has(key) ? this.repositionCache.get(key) : null;
                        if (sec != null && Number(sec) > 0) vehicleRepositionSecById[id] = Number(sec);
                    }
                    vehicles.push(v);
                    if (this.spec[id]) specById[id] = this.spec[id];
                }
                if (!vehicles.length) return null;
                const result = { vehicles, vehicleSpecById: specById };
                if (Object.keys(vehicleStartById).length) {
                    result.vehicleStartById = vehicleStartById;
                }
                if (Object.keys(vehicleRepositionSecById).length) {
                    result.vehicleRepositionSecById = vehicleRepositionSecById;
                }
                return result;
            },
            nextVehicleIdStart() {
                return this.maxVehicleId > 0 ? this.maxVehicleId + 1 : 1;
            },
        };
    },
    /**
     * Impede roteirização inválida: sem paradas geocodificadas, parada igual ao destino ou à base inicial.
     * Em erro, exibe mensagem e retorna { error: true }.
     * @param {Array} stops paradas já geocodificadas
     * @param {{ notGeocodedCount?: number }} [opts] quantidade de endereços sem coordenadas
     */
    validateStops(stops, opts = {}) {
        if (stops.length === 0) {
            const count = Number.isFinite(Number(opts.notGeocodedCount))
                ? Number(opts.notGeocodedCount)
                : 0;
            this.showError({
                message: this.$t('routePlanning.validation.noStopsGeocoded', { count }),
            });
            return { error: true };
        }
        if (stops.some(stop => this.destination.lat_centro == stop.lat && this.destination.lng_centro == stop.lng)) {
            this.showError({ message: this.$t('routePlanning.validation.destinationIsStop') });
            return { error: true };
        }
        if (
            this.initialBaseStop
            && this.initialBase
            && this.initialBase.lat_centro != null
            && this.initialBase.lng_centro != null
            && stops.some(stop => this.initialBase.lat_centro == stop.lat && this.initialBase.lng_centro == stop.lng)
        ) {
            this.showError({ message: this.$t('routePlanning.validation.initialBaseIsStop') });
            return { error: true };
        }
        return null;
    },
    /**
     * Coordenadas [lng, lat] de início/fim de viagem para o Verso: ida usa garagem→CD; volta inverte.
     * Inclui maxTripSec derivado do formulário.
     */
    tripBases(isVolta, arrivalTimeStr) {
        const maxTripSec = this.timeToSeconds(this.maxTripDuration);
        const arrivalTimeSec = this.timeToSeconds(arrivalTimeStr);
        const idaStartBase =
            this.initialBaseStop
            && this.initialBase
            && this.initialBase.lng_centro != null
            && this.initialBase.lat_centro != null
                ? [Number(this.initialBase.lng_centro), Number(this.initialBase.lat_centro)]
                : null;
        const idaEndBase =
            this.destination && this.destination.lng_centro != null && this.destination.lat_centro != null
                ? [Number(this.destination.lng_centro), Number(this.destination.lat_centro)]
                : null;
        const startBase = isVolta ? idaEndBase : idaStartBase;
        const endBase = isVolta ? idaStartBase : idaEndBase;
        return { maxTripSec, arrivalTime: arrivalTimeStr, arrivalTimeSec, idaStartBase, idaEndBase, startBase, endBase };
    },
    /**
     * Reanexa servedAddresses aos solveStops retornados pela API (que podem ter perdido o campo
     * no consolidate/split), casando por lat/lng com as paradas enviadas (mapStops).
     */
    attachServedAddressesToSolveStops(solveStops, sourceStops) {
        if (!Array.isArray(solveStops) || !solveStops.length) return solveStops;
        if (!Array.isArray(sourceStops) || !sourceStops.length) return solveStops;
        const byCoord = new Map();
        for (const s of sourceStops) {
            if (!s || !Number.isFinite(Number(s.lat)) || !Number.isFinite(Number(s.lng))) continue;
            const addrs = Array.isArray(s.servedAddresses)
                ? s.servedAddresses.map((a) => String(a || '').trim()).filter(Boolean)
                : [];
            if (!addrs.length) continue;
            const key = `${Number(s.lat).toFixed(6)}|${Number(s.lng).toFixed(6)}`;
            if (!byCoord.has(key)) {
                byCoord.set(key, addrs.slice());
            } else {
                byCoord.set(key, Array.from(new Set([...(byCoord.get(key) || []), ...addrs])));
            }
        }
        if (!byCoord.size) return solveStops;
        for (const s of solveStops) {
            if (!s || (Array.isArray(s.servedAddresses) && s.servedAddresses.length)) continue;
            if (!Number.isFinite(Number(s.lat)) || !Number.isFinite(Number(s.lng))) continue;
            const key = `${Number(s.lat).toFixed(6)}|${Number(s.lng).toFixed(6)}`;
            const addrs = byCoord.get(key);
            if (addrs && addrs.length) {
                s.servedAddresses = addrs.slice();
            }
        }
        return solveStops;
    },
    /**
     * Monta o array jobs (id, location, service, pickup) no formato esperado pela API Verso
     * /solve e /plan. Na ida as paradas são de embarque; na volta, de desembarque — tem que
     * bater com o que getsolve.js monta, senão o /plan devolve outro `service` e as rotas
     * aprovadas no solve são reprovadas em assertFeasible.
     */
    buildJobs(solveStops, isVolta = false) {
        const cfg = isVolta ? this.alightServiceConfig : this.stopServiceConfig;
        return solveStops.map((s, i) => ({
            id: i + 1,
            location: [Number(s.lng), Number(s.lat)],
            service: jobServiceSeconds(s.passengerAmount, cfg),
            pickup: [Math.max(1, parseInt(String(s.passengerAmount), 10) || 1)],
        }));
    },
    /** Serviço do terminal de uma rota do payload Verso, casando job.id com a parada. */
    terminalServiceSecForRouteSteps(versoRoute, solveStops, isVolta = false) {
        const paradas = Array.isArray(solveStops) ? solveStops : [];
        const doVeiculo = ((versoRoute && versoRoute.steps) || [])
            .filter((s) => this.isJobStep(s))
            .map((s) => paradas[Number(s.id) - 1])
            .filter(Boolean);
        return this.terminalServiceSecForStops(doVeiculo, isVolta);
    },
    terminalServiceSecForStops(stops, isVolta = false) {
        const cfg = isVolta ? this.stopServiceConfig : this.alightServiceConfig;
        const passageiros = (stops || []).reduce(
            (soma, s) => soma + Math.max(1, parseInt(String(s && s.passengerAmount), 10) || 1),
            0,
        );
        if (!passageiros) return 0;
        return jobServiceSeconds(passageiros, cfg);
    },
    /** Ids de job que o /solve devolveu explicitamente como não alocados. */
    unassignedJobIdsFromSolve(solvePayload) {
        const ids = new Set();
        for (const u of (solvePayload && solvePayload.unassigned) || []) {
            const jobId = u && u.id != null ? Number(u.id) : Number(u && u.job);
            if (Number.isFinite(jobId)) ids.add(jobId);
        }
        return ids;
    },
    buildPlanRequest(versoBody, solvePayload, fallbackJobs = []) {
        const bodyJobs = (versoBody && versoBody.jobs) || [];
        let planJobs = bodyJobs.length ? bodyJobs : fallbackJobs;
        // `versoBody.jobs` vem do solve com TODOS os jobs (inclusive os não alocados).
        // /plan só pode receber os que o solve conseguiu rotear.
        const assignedJobIds = new Set();
        for (const route of (solvePayload && solvePayload.routes) || []) {
            for (const st of (route.steps || [])) {
                if (!this.isJobStep(st)) continue;
                const jobId = st.id != null ? Number(st.id) : Number(st.job);
                if (Number.isFinite(jobId)) assignedJobIds.add(jobId);
            }
        }
        // Exclusão explícita pela lista `unassigned` do solve: não depende de
        // `assignedJobIds` ter sido preenchido (se nenhum step do solve for
        // reconhecido como job, o filtro por "assigned" abaixo não roda e os
        // não alocados vazariam para o /plan).
        const unassignedJobIds = this.unassignedJobIdsFromSolve(solvePayload);
        if (unassignedJobIds.size) {
            planJobs = planJobs.filter((j) => !unassignedJobIds.has(Number(j.id)));
        }
        if (assignedJobIds.size) {
            planJobs = planJobs.filter((j) => assignedJobIds.has(Number(j.id)));
        }
        const solveVehicles = (versoBody && versoBody.vehicles) || [];
        const planVehicles = (solvePayload.routes || []).map((route) => {
            const vehicleId = Number(route.vehicle);
            const base = solveVehicles.find((v) => Number(v.id) === vehicleId) || {};
            const steps = (route.steps || []).map((st) => {
                const type = String(st.type || '').toLowerCase();
                if (type === 'start' || type === 'end') {
                    const step = { type };
                    if (Array.isArray(st.location) && st.location.length >= 2) {
                        step.location = [Number(st.location[0]), Number(st.location[1])];
                    }
                    return step;
                }
                if (this.isJobStep(st)) {
                    const jobId = st.id != null ? Number(st.id) : Number(st.job);
                    // Nunca referencia no /plan um job que o solve não alocou.
                    if (Number.isFinite(jobId) && !unassignedJobIds.has(jobId)) {
                        return { type: 'job', id: jobId };
                    }
                }
                return null;
            }).filter(Boolean);
            // Veículo que ficou só com start/end (todos os jobs dele eram não
            // alocados) não vai ao /plan — rota vazia não produz trecho algum.
            if (!steps.some((st) => st.type === 'job')) {
                return null;
            }
            const vehicle = {
                id: vehicleId,
                profile: base.profile || 'car',
                capacity: Array.isArray(base.capacity) && base.capacity.length
                    ? base.capacity.map((n) => Number(n))
                    : null,
                start: Array.isArray(base.start) && base.start.length === 2
                    ? [Number(base.start[0]), Number(base.start[1])]
                    : undefined,
                end: Array.isArray(base.end) && base.end.length === 2
                    ? [Number(base.end[0]), Number(base.end[1])]
                    : undefined,
                steps,
            };
            if (base.max_travel_time != null) {
                vehicle.max_travel_time = Number(base.max_travel_time);
            }
            if (!vehicle.capacity) {
                return null;
            }
            if (!vehicle.start || !vehicle.end) {
                const startSt = (route.steps || []).find((s) => String(s.type || '').toLowerCase() === 'start');
                const endSt = (route.steps || []).find((s) => String(s.type || '').toLowerCase() === 'end');
                if (!vehicle.start && startSt && Array.isArray(startSt.location) && startSt.location.length >= 2) {
                    vehicle.start = [Number(startSt.location[0]), Number(startSt.location[1])];
                }
                if (!vehicle.end && endSt && Array.isArray(endSt.location) && endSt.location.length >= 2) {
                    vehicle.end = [Number(endSt.location[0]), Number(endSt.location[1])];
                }
            }
            if (!vehicle.start && !vehicle.end) {
                return null;
            }
            return vehicle;
        }).filter(Boolean);
        return { jobs: planJobs, vehicles: planVehicles };
    },
    /**
     * Valida resposta do solve: nenhuma rota acima do tempo máximo e nenhum job sem veículo (unassigned).
     * Com `allowUnassigned`, unassigned vira soft-fail (partialUnassigned) em vez de erro — as rotas
     * montadas seguem e as paradas sem encaixe vão para a aba de não roteirizadas. Rota que estoura o
     * tempo máximo no cálculo do cliente sai inteira (paradas viram não alocadas); só se nenhuma rota
     * sobrar o grupo falha.
     */
    assertFeasible(versoPayload, maxTripSec, tripOpts = {}) {
        const {
            hasStartBase = false,
            hasEndBase = false,
            isVolta = false,
            skipGarageDepartureVehicleIds = null,
            garageReturnVehicleIds = null,
            repositionStartVehicleIds = null,
            allowUnassigned = false,
        } = tripOpts;
        const durationOpts = {
            hasStartBase,
            hasEndBase,
            isVolta,
            skipGarageDepartureVehicleIds,
            garageReturnVehicleIds,
            repositionStartVehicleIds,
        };
        const versoPayloadRoutes = versoPayload.routes;
        if (maxTripSec != null && versoPayloadRoutes.length) {
            const violating = new Set();
            for (let i = 0; i < versoPayloadRoutes.length; i++) {
                const dur = this.getRouteDuration(versoPayloadRoutes[i], {
                    ...durationOpts,
                    terminalServiceSec: this.terminalServiceSecForRouteSteps(
                        versoPayloadRoutes[i], tripOpts.solveStops, isVolta,
                    ),
                });
                if (dur != null && dur > maxTripSec) violating.add(i);
            }
            if (violating.size) {
                const keptRoutes = versoPayloadRoutes.filter((_, i) => !violating.has(i));
                if (!allowUnassigned || !keptRoutes.length) {
                    return {
                        error: true,
                        failureReason: 'MAX_TRIP_EXCEEDED',
                        // Nenhuma rota sobrou: marca o grupo inteiro como não-alocado (ver
                        // buildUnassignedStopsFromAllSolveStops) em vez de deixá-lo sumir.
                        unassignedStops: this.buildUnassignedStopsFromAllSolveStops(
                            tripOpts.solveStops,
                            tripOpts.sourceStops,
                        ),
                        solveStops: tripOpts.solveStops || null,
                    };
                }
                // Só as rotas que estouraram saem; as demais do grupo seguem roteirizadas e as
                // paradas retiradas vão para a aba de não roteirizadas.
                const droppedJobs = [];
                versoPayloadRoutes.forEach((route, i) => {
                    if (!violating.has(i)) return;
                    (route.steps || []).forEach((st) => {
                        if (String(st.type || '').toLowerCase() === 'job' && st.id != null) {
                            droppedJobs.push({ id: st.id, type: 'job', location: st.location });
                        }
                    });
                });
                const unassigned = [...(versoPayload.unassigned || []), ...droppedJobs];
                const trimmedPayload = {
                    ...versoPayload,
                    routes: keptRoutes,
                    unassigned,
                    summary: {
                        ...(versoPayload.summary || {}),
                        routes: keptRoutes.length,
                        unassigned: unassigned.length,
                    },
                };
                return {
                    partialUnassigned: true,
                    failureReason: 'MAX_TRIP_EXCEEDED',
                    unassignedStops: this.buildUnassignedStopsFromPayload(
                        trimmedPayload,
                        tripOpts.solveStops,
                        tripOpts.sourceStops,
                    ),
                    versoPayload: trimmedPayload,
                    solveStops: tripOpts.solveStops || null,
                };
            }
        }
        const unassignedCount = (() => {
            const uArr = versoPayload.unassigned;
            if (versoPayload.summary && versoPayload.summary.unassigned != null) {
                return Number(versoPayload.summary.unassigned);
            }
            return uArr.length;
        })();
        if (unassignedCount > 0) {
            const unassignedStops = this.buildUnassignedStopsFromPayload(
                versoPayload,
                tripOpts.solveStops,
                tripOpts.sourceStops,
            );
            if (allowUnassigned) {
                return {
                    partialUnassigned: true,
                    failureReason: 'MAX_TRIP_EXCEEDED',
                    unassignedStops,
                    solveStops: tripOpts.solveStops || null,
                };
            }
            return {
                error: true,
                failureReason: 'INSUFFICIENT_FLEET',
                unassignedStops,
                solveStops: tripOpts.solveStops || null,
            };
        }
        return null;
    },
    /**
     * Normaliza payload Verso, aplica split de deslocamento, valida rotas e loga composição da frota no modo AUTO.
     */
    async finalizeRoutes(params) {
        const {
            versoPayload,
            solvePayload,
            solveStops,
            numVeiculos,
            hasStartBase,
            hasEndBase,
            isVolta,
            autoFleetMode,
            vehicleSpecById,
            displacementFlags,
        } = params;
        const sentidoExtensoEffective = isVolta
            ? this.$t('routePlanning.direction.volta')
            : this.$t('routePlanning.direction.ida');
        let responseData = this.normalizeVersoRoutes(
            versoPayload,
            solveStops,
            numVeiculos,
            hasStartBase,
            sentidoExtensoEffective,
            hasEndBase,
            displacementFlags,
        );
        if ((!responseData.routes || responseData.routes.length === 0) && versoPayload !== solvePayload) {
            responseData = this.normalizeVersoRoutes(
                solvePayload,
                solveStops,
                numVeiculos,
                hasStartBase,
                sentidoExtensoEffective,
                hasEndBase,
                displacementFlags,
            );
        }
        if (responseData.error) {
            this.showError(responseData);
            return { error: true };
        }
        if (!responseData.routes || responseData.routes.length === 0) {
            this.showError({ message: this.$t('routePlanning.validation.noRoutesFound') });
            return { error: true };
        }
        this.splitDisplacement(responseData);
        if (autoFleetMode && this.token) {
            try {
                await this._summarizeAutoFleet(responseData.routes, vehicleSpecById);
            } catch (e) {
                // falha tolerada
            }
        } else {
            this.fleetCostSummary = null;
        }
        return { responseData };
    },
    /**
     * Refina rotas com POST /verso/plan (geometria detalhada); se falhar ou vier vazio, mantém o resultado do /solve.
     */
    async tryPlan(params) {
        const {
            jobs,
            versoBody,
            versoPayload: initialVersoPayload,
            solveStops,
            numVeiculos,
            hasStartBase,
            hasEndBase,
            isVolta,
            maxTripSec,
            skipGarageDepartureVehicleIds,
            garageReturnVehicleIds,
            repositionStartVehicleIds,
        } = params;
        let versoPayload = initialVersoPayload;
        try {
            const { jobs: planJobs, vehicles: planVehicles } = this.buildPlanRequest(
                versoBody,
                versoPayload,
                jobs,
            );
            if (!planJobs.length || !planVehicles.length) {
                return versoPayload;
            }
            const planData = {
                jobs: planJobs,
                vehicles: planVehicles,
            };
            if (maxTripSec != null && Number.isFinite(Number(maxTripSec)) && Number(maxTripSec) > 0) {
                planData.maxTripSec = Number(maxTripSec);
                planData.hasStartBase = !!hasStartBase;
                planData.hasEndBase = !!hasEndBase;
                planData.isVolta = !!isVolta;
                planData.initialBaseStop = this.initialBaseStop;
                // O /plan revalida o tempo máximo: sem a cfg do terminal ele ignoraria o
                // desembarque no CD e aprovaria rota que o /solve reprovou.
                const cfgTerminal = isVolta ? this.stopServiceConfig : this.alightServiceConfig;
                planData.terminalServiceMode = cfgTerminal.mode;
                planData.terminalServiceFixedSec = cfgTerminal.fixedSec;
                planData.terminalServicePerPassengerSec = cfgTerminal.perPassengerSec;
                if (Array.isArray(skipGarageDepartureVehicleIds)) {
                    planData.skipGarageDepartureVehicleIds = skipGarageDepartureVehicleIds;
                }
                if (Array.isArray(garageReturnVehicleIds)) {
                    planData.garageReturnVehicleIds = garageReturnVehicleIds;
                }
                if (Array.isArray(repositionStartVehicleIds) && repositionStartVehicleIds.length) {
                    planData.repositionStartVehicleIds = repositionStartVehicleIds;
                }
            }
            // Criação de rotas: o plan do grupo (com o laço do tempo máximo) roda no scheduler.
            const planRes = await this._requestVersoPlanScheduled(planData);
            const planned = planRes.data;
            // O /plan retirou paradas para respeitar o tempo máximo: o /solve já validou o
            // grupo inteiro contra maxTripSec (e realocou o que não coube), então segue com
            // o resultado do solve em vez de perder passageiros por diferença de cálculo.
            if (planned && Array.isArray(planned.trimmedJobIds) && planned.trimmedJobIds.length) {
                return { error: true, failureReason: 'MAX_TRIP_EXCEEDED' };
            }
            if (planned && planned.code === 0 && planned.routes) {
                const plannedNorm = this.normalizeVersoRoutes(
                    planned,
                    solveStops,
                    numVeiculos,
                    hasStartBase,
                    isVolta ? this.$t('routePlanning.direction.volta') : null,
                    hasEndBase,
                );
                if (plannedNorm.routes && plannedNorm.routes.length > 0) {
                    // /plan pode omitir `unassigned` do solve — preserva os jobs não alocados do VROOM.
                    const initialUnassigned = Array.isArray(initialVersoPayload && initialVersoPayload.unassigned)
                        ? initialVersoPayload.unassigned
                        : [];
                    const plannedUnassigned = Array.isArray(planned.unassigned) ? planned.unassigned : [];
                    const mergedUnassigned = initialUnassigned.length ? initialUnassigned : plannedUnassigned;
                    const initialUnassignedCount = (() => {
                        if (initialVersoPayload && initialVersoPayload.summary
                            && initialVersoPayload.summary.unassigned != null) {
                            return Number(initialVersoPayload.summary.unassigned);
                        }
                        return initialUnassigned.length;
                    })();
                    versoPayload = {
                        ...planned,
                        unassigned: mergedUnassigned,
                        summary: {
                            ...(planned.summary || {}),
                            unassigned: initialUnassignedCount > 0
                                ? initialUnassignedCount
                                : ((planned.summary && planned.summary.unassigned != null)
                                    ? planned.summary.unassigned
                                    : mergedUnassigned.length),
                        },
                    };
                }
            }
        } catch (planErr) {
            const planStatus = planErr && planErr.response && planErr.response.status;
            const planCodigo = planErr && planErr.response && planErr.response.data && planErr.response.data.codigo;
            const planMsg = planErr && planErr.response && planErr.response.data && planErr.response.data.message;
            if (planStatus === 429 || planCodigo === 'RATE_LIMIT_VROOM') {
                this.showError({ message: this.$t('routePlanning.validation.rateLimit') });
                return { error: true };
            }
            if (this.isVersoServerFailure(planErr)) {
                return {
                    error: true,
                    failureReason: 'SERVER_ERROR',
                    failureDetail: this.versoServerFailureDetail(planErr),
                };
            }
            if (planStatus === 422) {
                const planFailureMsg = planMsg || this.$t('routePlanning.validation.noRouteInDesiredTime');
                return {
                    error: true,
                    failureReason: this.classifyRoutingFailureReason(planFailureMsg) || 'MAX_TRIP_EXCEEDED',
                };
            }
        }
        return versoPayload;
    },
    getSelectedOperationalFleetTypes() {
        const selected = new Set((this.selectedOperationalVehicleTypes || []).map((id) => Number(id)));
        const validas = (this.operationalVehicleTypeRows || [])
            .filter((row) => selected.has(Number(row.id_tipo_veiculo)))
            .map((row) => ({
                row,
                item: {
                    key: String(row.id_tipo_veiculo),
                    type: row.descricao_tipo_veiculo != null
                        ? String(row.descricao_tipo_veiculo).trim()
                        : String(row.id_tipo_veiculo),
                    capacity: Math.max(1, Number(row.lotacao) || 1),
                    fixedCost: row.valor_fixo != null && row.valor_fixo !== '' ? row.valor_fixo : 0,
                    costPerKm: row.valor_variavel != null && row.valor_variavel !== '' ? row.valor_variavel : 0,
                    laborCost: row.valor_labor != null && row.valor_labor !== '' ? row.valor_labor : 0,
                    profile: 'car',
                },
            }))
            .filter(({ item }) => item.type && item.capacity > 0);
        // Balanço da unidade (percentual_balanco por tipo base) ou o que o usuário digitou na tela.
        // Sempre segue como sharePercent: a soma de 100% é exigida na tela e pelo solver, e tipo
        // com 0% não é ofertado.
        const shares = calcularSharePercentOperacional(
            validas.map(({ row }) => row),
            this.operationalFleetPercentOverrides,
        );
        return validas.map(({ item }, idx) => ({ sharePercent: shares[idx], ...item }));
    },
    async requestFleetSolve(stops, ctx) {
        const {
            startBase,
            endBase,
            maxTripSec,
            hasStartBase,
            hasEndBase,
            vehicleSpecById,
        } = ctx;
        const directionLabel = ctx.arrivalTime == null
            ? this.$t('routePlanning.direction.volta')
            : this.$t('routePlanning.direction.ida');
        try {
            const requestBody = {
                stops,
                startBase: startBase || null,
                endBase: endBase || null,
                maxTripSec: maxTripSec != null ? maxTripSec : null,
                arrivalTime: ctx.arrivalTime,
                hasStartBase,
                hasEndBase,
                isVolta: !!ctx.isVolta,
                initialBaseStop: this.initialBaseStop,
                directionLabel,
                displacementDirectionLabel: this.$t('routePlanning.direction.displacement'),
                stopServiceMode: this.stopServiceConfig.mode,
                stopServiceFixedSec: this.stopServiceConfig.fixedSec,
                stopServicePerPassengerSec: this.stopServiceConfig.perPassengerSec,
                alightServiceMode: this.alightServiceConfig.mode,
                alightServiceFixedSec: this.alightServiceConfig.fixedSec,
                alightServicePerPassengerSec: this.alightServiceConfig.perPassengerSec,
                // Grupo grande o backend manda para o scheduler (202); este bundle sabe acompanhar.
                allowScheduler: true,
            };
            if (ctx.fleetTypes && ctx.fleetTypes.length) {
                requestBody.fleetTypes = ctx.fleetTypes;
            } else if (this.isRoutingOperationalMode) {
                const fleetTypes = this.getSelectedOperationalFleetTypes();
                if (!fleetTypes.length) {
                    this.showError({
                        message: this.$t('routePlanning.validation.availableVehicleTypesRequired'),
                    });
                    return { error: true };
                }
                requestBody.fleetTypes = fleetTypes;
            } else {
                requestBody.fleetTypes = this.getSelectedSimulationFleetTypes();
            }
            if (ctx.reuseFrom) {
                requestBody.reuseFrom = ctx.reuseFrom;
            }
            if (ctx.vehicleIdStart != null && Number.isFinite(Number(ctx.vehicleIdStart))) {
                requestBody.vehicleIdStart = Number(ctx.vehicleIdStart);
            }
            if (ctx.maxVehicleTrips != null && Number.isFinite(Number(ctx.maxVehicleTrips))) {
                requestBody.maxVehicleTrips = Number(ctx.maxVehicleTrips);
            }
            // Transporte de quem hospeda: na tela, /verso/solve pelo scheduler; no worker, no processo.
            return await this._sendFleetSolve(requestBody, vehicleSpecById);
        } catch (e) {
            return this._mapFleetSolveError(
                e.response && e.response.status,
                e.response && e.response.data,
                e,
            );
        }
    },
    /** Resposta 200 do /verso/solve de frota (direta ou entregue pelo scheduler). */
    _mapFleetSolveData(data, vehicleSpecById) {
        if (data.error) {
            const msg = data.message || this.$t('routePlanning.validation.noRoutesFound');
            const serverFail = data.codigo === 'VERSO_SERVER_ERROR';
            return {
                error: true,
                failureReason: this.classifyRoutingFailureReason(msg, data.codigo),
                ...(serverFail ? { failureDetail: msg } : {}),
                unassignedStops: data.unassignedStops || null,
            };
        }
        Object.assign(vehicleSpecById, data.vehicleSpecById || {});
        Object.assign(this.fleetVehicleSpecById, data.vehicleSpecById || {});
        return {
            autoFleet: data.autoFleet,
            capacityCap: data.capacityCap != null ? data.capacityCap : null,
            totalFleetCapacityGrupo: data.totalFleetCapacityGrupo,
            solveStops: data.solveStops,
            numVeiculosSolve: data.numVeiculosSolve,
            precomputedVersoBody: data.precomputedVersoBody,
            precomputedSolvePayload: data.precomputedSolvePayload,
            partialAllocation: !!data.partialAllocation,
            unassignedStops: data.unassignedStops || null,
            unassignedPassengerCount: data.unassignedPassengerCount != null
                ? Number(data.unassignedPassengerCount)
                : null,
            failureReason: data.failureReason || null,
        };
    },
    /**
     * Resposta de erro (4xx/5xx) do /verso/solve de frota. `e` é o erro do axios no caminho
     * direto; no caminho do scheduler vem só `status` e `data`, gravados pelo worker.
     */
    _mapFleetSolveError(status, data, e = null) {
        const codigo = data && data.codigo;
        const msg =
            (data && data.message) ||
            (e && e.message) ||
            this.$t('routePlanning.loading.sendError');
        if (status === 429 || codigo === 'RATE_LIMIT_VROOM') {
            this.showError({ message: this.$t('routePlanning.validation.rateLimit') });
            return { error: true, rateLimited: true };
        }
        const falha = e || { response: { status, data } };
        if (this.isVersoServerFailure(falha)) {
            return {
                error: true,
                failureReason: 'SERVER_ERROR',
                failureDetail: this.versoServerFailureDetail(falha),
                unassignedStops: null,
            };
        }
        return {
            error: true,
            failureReason: this.classifyRoutingFailureReason(msg, codigo),
            unassignedStops: data && data.unassignedStops ? data.unassignedStops : null,
        };
    },
    /**
     * Remove de `stops` as paradas/jobs que constam em unassigned do VROOM
     * (não devem permanecer na tabela de paradas válidas).
     */
    filterStopsMatchingUnassigned(stops, unassignedStops, solveStops) {
        const list = Array.isArray(stops) ? stops : [];
        const unassigned = Array.isArray(unassignedStops) ? unassignedStops : [];
        if (!list.length || !unassigned.length) return list;
        const coordKeys = new Set();
        const addrKeys = new Set();
        for (const u of unassigned) {
            if (!u) continue;
            const solveStop = u.jobId != null && Array.isArray(solveStops)
                ? solveStops[Number(u.jobId) - 1]
                : null;
            const lat = u.lat != null ? u.lat : (solveStop && solveStop.lat);
            const lng = u.lng != null ? u.lng : (solveStop && solveStop.lng);
            const key = this.getLatLngKey(lat, lng);
            if (key) coordKeys.add(key);
            const pushAddr = (raw) => {
                const s = String(raw || '').trim().toLowerCase();
                if (s) addrKeys.add(s);
            };
            pushAddr(u.endereco);
            pushAddr(u.nome);
            (Array.isArray(u.servedAddresses) ? u.servedAddresses : []).forEach(pushAddr);
            if (solveStop) {
                pushAddr(solveStop.name);
                (Array.isArray(solveStop.servedAddresses) ? solveStop.servedAddresses : []).forEach(pushAddr);
            }
        }
        if (!coordKeys.size && !addrKeys.size) return list;
        return list.filter((stop) => {
            if (!stop) return false;
            const key = this.getLatLngKey(
                stop.latitude != null ? stop.latitude : stop.lat,
                stop.longitude != null ? stop.longitude : stop.lng,
            );
            if (key && coordKeys.has(key)) return false;
            const candidates = [];
            if (stop.endereco) candidates.push(stop.endereco);
            const resolved = this.resolveOriginalPointAddress(stop);
            if (resolved) candidates.push(resolved);
            for (const a of (stop.servedAddresses || [])) candidates.push(a);
            for (const a of candidates) {
                const s = String(a || '').trim().toLowerCase();
                if (s && addrKeys.has(s)) return false;
            }
            return true;
        });
    },
    /**
     * Classifica falha de roteirização: frota insuficiente vs tempo máximo de viagem.
     * @returns {'INSUFFICIENT_FLEET'|'MAX_TRIP_EXCEEDED'|null}
     */
    classifyRoutingFailureReason(message, codigo = null) {
        // O código é a fonte confiável; o texto é fallback para respostas antigas.
        if (codigo === 'FLEET_LIMIT_VROOM') return 'FLEET_LIMIT';
        if (codigo === 'VERSO_SERVER_ERROR') return 'SERVER_ERROR';
        const text = String(message || '').toLowerCase();
        if (!text) return null;
        // Antes do bloco de tempo máximo: a mensagem antiga do 413 falava em "tempo
        // máximo de viagem" e caía no diagnóstico errado.
        if (
            text.includes('excesso de veículos')
            || text.includes('excesso de veiculos')
            || text.includes('exceso de vehículos')
            || text.includes('exceso de vehiculos')
            || text.includes('limite do verso')
            || text.includes('límite del verso')
        ) {
            return 'FLEET_LIMIT';
        }
        if (
            text.includes('tempo máximo de viagem')
            || text.includes('tempo maximo de viagem')
            || text.includes('tiempo máximo de viaje')
            || text.includes('tiempo maximo de viaje')
            || text.includes('respeitar o tempo máximo')
            || text.includes('respetar el tiempo máximo')
            || text.includes('não atendem ao tempo máximo')
            || text.includes('nao atendem ao tempo maximo')
            || text.includes('no atienden al tiempo máximo')
        ) {
            return 'MAX_TRIP_EXCEEDED';
        }
        if (
            text.includes('frota disponível insuficiente')
            || text.includes('frota disponivel insuficiente')
            || text.includes('flota disponible insuficiente')
            || text.includes('não alocada')
            || text.includes('nao alocada')
            || text.includes('no asignada')
            || text.includes('capacidade da frota')
            || text.includes('capacidad de la flota')
            || text.includes('limite seguro')
            || text.includes('límite seguro')
            || text.includes('unassigned')
            || text.includes('passageiros excedem')
            || text.includes('pasajeros exceden')
        ) {
            return 'INSUFFICIENT_FLEET';
        }
        return null;
    },
    /** Falha de servidor/rede do Vroom (esgotados os retries do backend). */
    isVersoServerFailure(err) {
        if (!err) return false;
        const data = err.response && err.response.data;
        if (data && data.codigo === 'VERSO_SERVER_ERROR') return true;
        if (!err.response) {
            return ['ETIMEDOUT', 'ECONNABORTED', 'ECONNRESET', 'ECONNREFUSED', 'ERR_NETWORK'].includes(err.code)
                || err.message === 'Network Error';
        }
        return Number(err.response.status) >= 502 && Number(err.response.status) <= 504;
    },
    versoServerFailureDetail(err) {
        const data = err && err.response && err.response.data;
        const raw = (data && typeof data === 'object' && data.message) || (err && err.message) || '';
        return String(raw).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },
    /** Monta 1 item de "unassigned" (mesmo formato usado na tabela de não-alocados) a partir de 1 solveStop. */
    _buildUnassignedStopEntry(id, solveStops, sourceStops) {
        const solveStop = (solveStops || [])[id - 1];
        let servedAddresses = Array.isArray(solveStop && solveStop.servedAddresses)
            ? solveStop.servedAddresses.slice()
            : [];
        let src = this.findSourceStopByCoord(solveStop, sourceStops);
        if (!src && servedAddresses.length && Array.isArray(sourceStops)) {
            const addrSet = new Set(
                servedAddresses.map((a) => String(a || '').trim().toLowerCase()).filter(Boolean),
            );
            src = sourceStops.find((s) => {
                if (!s) return false;
                const addr = this.resolveOriginalPointAddress(s);
                return addr && addrSet.has(addr.toLowerCase());
            }) || null;
        }
        if (!servedAddresses.length && src) {
            const addr = this.resolveOriginalPointAddress(src);
            if (addr) servedAddresses = [addr];
        }
        if (!servedAddresses.length && solveStop && solveStop.name) {
            const name = String(solveStop.name).trim();
            if (name && !this.isCoordOnlyAddress(name)) servedAddresses = [name];
        }
        return {
            jobId: id,
            nome: solveStop && solveStop.name != null ? String(solveStop.name) : null,
            lat: solveStop && solveStop.lat,
            lng: solveStop && solveStop.lng,
            passageiros: solveStop && solveStop.passengerAmount != null
                ? Number(solveStop.passengerAmount)
                : null,
            endereco: src ? this.resolveOriginalPointAddress(src) : null,
            servedAddresses,
        };
    },
    buildUnassignedStopsFromPayload(versoPayload, solveStops, sourceStops) {
        const ids = [];
        for (const u of (versoPayload && versoPayload.unassigned) || []) {
            if (u && u.id != null) ids.push(Number(u.id));
            else if (u && u.job != null) ids.push(Number(u.job));
        }
        if (!ids.length) return [];
        return ids.map((id) => this._buildUnassignedStopEntry(id, solveStops, sourceStops));
    },
    /**
     * Quando o grupo inteiro falha sem detalhamento de quais jobs ficaram sem rota
     * (ex.: /plan rejeitado com 422 por tempo máximo de viagem, ou uma rota do plan
     * violando maxTripSec detectada só no cliente) não dá pra saber QUAL parada
     * especificamente causou o estouro. Em vez de descartar o grupo inteiro
     * silenciosamente — paradas que não aparecem roteirizadas nem na aba "não
     * alocadas", simplesmente somem —, trata todo mundo do grupo como não-alocado
     * pra manter visibilidade. Grosseiro (pode marcar como não-alocada gente que
     * teria dado certo numa rota diferente), mas nunca perde dado silenciosamente.
     */
    buildUnassignedStopsFromAllSolveStops(solveStops, sourceStops) {
        const list = Array.isArray(solveStops) ? solveStops : [];
        return list.map((_, idx) => this._buildUnassignedStopEntry(idx + 1, solveStops, sourceStops));
    },
    /**
     * Mesmo papel do acima, mas quando nem `solveStops` existe: o /solve falhou antes
     * de devolver payload (ex.: 422 de tempo máximo de viagem em frota automática, em
     * que ninguém fica `unassigned` e o backend não manda `unassignedStops`). Sem isto
     * o grupo some: não aparece roteirizado nem na aba de não roteirizadas.
     */
    buildUnassignedStopsFromSourceStops(sourceStops) {
        const list = Array.isArray(sourceStops) ? sourceStops : [];
        return list.map((s, idx) => {
            const lat = Number(s && (s.lat != null ? s.lat : s.latitude));
            const lng = Number(s && (s.lng != null ? s.lng : s.longitude));
            const endereco = s ? this.resolveOriginalPointAddress(s) : null;
            const passageiros = Number(s && s.passengerAmount);
            return {
                jobId: idx + 1,
                nome: endereco,
                lat: Number.isFinite(lat) ? lat : null,
                lng: Number.isFinite(lng) ? lng : null,
                passageiros: Number.isFinite(passageiros) && passageiros > 0 ? passageiros : null,
                endereco,
                servedAddresses: endereco ? [endereco] : [],
            };
        });
    },
    findSourceStopByCoord(solveStop, sourceStops) {
        if (!solveStop || !Array.isArray(sourceStops)) return null;
        if (!Number.isFinite(Number(solveStop.lat)) || !Number.isFinite(Number(solveStop.lng))) return null;
        const key = `${Number(solveStop.lat).toFixed(6)}|${Number(solveStop.lng).toFixed(6)}`;
        return sourceStops.find((s) => {
            if (!s || !Number.isFinite(Number(s.lat)) || !Number.isFinite(Number(s.lng))) return false;
            return `${Number(s.lat).toFixed(6)}|${Number(s.lng).toFixed(6)}` === key;
        }) || null;
    },
    /**
     * Índice do vértice da polyline mais próximo da parada (base → 1ª parada).
     */
    _nearestSplitIndex(latlngs, stopLat, stopLng) {
        if (!latlngs?.length) return 0;
        const tLat = Number(stopLat);
        const tLng = Number(stopLng);
        let bestI = 0;
        let best = Infinity;
        for (let i = 0; i < latlngs.length; i++) {
            const p = latlngs[i];
            const d = (p.lat - tLat) * (p.lat - tLat) + (p.lng - tLng) * (p.lng - tLng);
            if (d < best) {
                best = d;
                bestI = i;
            }
        }
        return bestI;
    },
    /**
     * Com hasStartbase: rota de deslocamento só polyline + métricas base→1ª parada (sem paradas na tabela);
     * rota de ida mantém todas as paradas, métricas e trecho de polyline após a 1ª parada.
     */
    splitDisplacement(responseData) {
        if (!responseData || !Array.isArray(responseData.routes) || responseData.routes.length === 0) return;

        const newRoutes = [];
        for (let i = 0; i < responseData.routes.length; i++) {
            const r = responseData.routes[i];
            const vr = r.versoRoute;
            const isEndBaseSplit = !!r.hasEndbase;
            if ((!r.hasStartbase && !isEndBaseSplit) || !r.stops || r.stops.length === 0) {
                newRoutes.push(r);
                continue;
            }
            if (!vr) {
                newRoutes.push(r);
                continue;
            }
            const firstStop = r.stops[0];
            const lastStop = r.stops[r.stops.length - 1];
            const allStopsOnMainRoute = r.stops;
            const allSteps = vr.steps || [];
            const startStep = allSteps.find(s => String(s.type || '').toLowerCase() === 'start') || {};
            const endStep = allSteps.find(s => String(s.type || '').toLowerCase() === 'end');
            const jobSteps = allSteps.filter(s => this.isJobStep(s));
            const j0 = jobSteps[0];
            const jN = jobSteps[jobSteps.length - 1];
            if (!j0 || !jN) {
                newRoutes.push(r);
                continue;
            }

            let origDur = parseDurationSeconds(r.durationSec);
            if (!Number.isFinite(origDur) || origDur <= 0) {
                origDur = parseDurationSeconds(vr.duration != null ? vr.duration : (vr.summary && vr.summary.duration));
            }
            const origDist = Number(r.distance) || 0;

            const startDist = startStep.distance != null ? Number(startStep.distance) : 0;
            const startDur = startStep.duration != null ? Number(startStep.duration) : 0;
            const pivotStep = isEndBaseSplit ? jN : j0;
            const pivotStop = isEndBaseSplit ? lastStop : firstStop;
            const pivotDist = pivotStep.distance != null ? Number(pivotStep.distance) : NaN;
            const pivotDur = pivotStep.duration != null ? Number(pivotStep.duration) : NaN;
            const pivotArr = pivotStep.arrival != null ? Number(pivotStep.arrival) : NaN;
            const endDist = endStep && endStep.distance != null ? Number(endStep.distance) : origDist;
            const endDur = endStep && endStep.arrival != null ? Number(endStep.arrival) : origDur;

            let dispDist = !Number.isNaN(pivotDist)
                ? (isEndBaseSplit ? Math.max(0, endDist - pivotDist) : Math.max(0, pivotDist - startDist))
                : 0;
            let mainDist = !Number.isNaN(pivotDist)
                ? (isEndBaseSplit ? Math.max(0, pivotDist - startDist) : Math.max(0, endDist - pivotDist))
                : origDist;
            let dispDur = !Number.isNaN(pivotArr)
                ? (isEndBaseSplit ? Math.max(0, endDur - pivotArr) : Math.max(0, pivotDur - startDur))
                : 0;
            let mainDur = !Number.isNaN(pivotArr)
                ? (isEndBaseSplit ? Math.max(0, pivotArr - startDur) : Math.max(0, endDur - pivotDur))
                : origDur;

            if ((dispDist === 0 && mainDist === 0 && origDist > 0) || (dispDur === 0 && mainDur === 0 && origDur > 0)) {
                const dStart = Number(startDur) || 0;
                const dPivot = !Number.isNaN(pivotArr) ? pivotArr : dStart;
                const dEnd = Number(endStep && endStep.arrival);
                
                let tBefore = Math.max(0, dPivot - dStart);
                let tAfter = !Number.isNaN(dEnd) ? Math.max(0, dEnd - dPivot) : 0;

                // Fallback: se não há steps de start/end, estimar deslocamento via Haversine
                if (tBefore === 0 && tAfter === 0 && this.initialBase && pivotStop.lat != null) {
                    const distM = haversineMeters(
                        this.initialBase.lat_centro, this.initialBase.lng_centro,
                        pivotStop.lat, pivotStop.lng
                    );
                    const estimatedDispDist = Math.round(distM * 1.3);
                    const estimatedDispDur = Math.round(estimatedDispDist / 13);
                    
                    if (isEndBaseSplit) {
                        tAfter = estimatedDispDur;
                        tBefore = origDur; 
                    } else {
                        tBefore = estimatedDispDur;
                        tAfter = origDur;
                    }
                }

                const tMain = isEndBaseSplit ? tBefore : tAfter;
                const tDisp = isEndBaseSplit ? tAfter : tBefore;
                const sumT = tMain + tDisp;
                if (sumT > 0 && origDist > 0 && dispDist === 0 && mainDist === 0) {
                    dispDist = origDist * (tDisp / sumT);
                    mainDist = origDist * (tMain / sumT);
                }
                if (sumT > 0 && origDur > 0 && dispDur === 0 && mainDur === 0) {
                    dispDur = origDur * (tDisp / sumT);
                    mainDur = origDur * (tMain / sumT);
                }
            }

            let encDisp = null;
            let encMain = null;
            const fullEnc = r.geometry;
            if (fullEnc && pivotStop.lat != null && pivotStop.lng != null) {
                try {
                    const latlngs = decodeToLatLngs(fullEnc);
                    if (latlngs && latlngs.length >= 2) {
                        const splitIdx = this._nearestSplitIndex(latlngs, pivotStop.lat, pivotStop.lng);
                        const partA = latlngs.slice(0, splitIdx + 1);
                        const partB = latlngs.slice(splitIdx);
                        if (partA.length >= 2 && partB.length >= 2) {
                            if (isEndBaseSplit) {
                                encMain = encodeLatLngs(partA);
                                encDisp = encodeLatLngs(partB);
                            } else {
                                encDisp = encodeLatLngs(partA);
                                encMain = encodeLatLngs(partB);
                            }
                        }
                    }
                } catch (e) {
                    encDisp = null;
                    encMain = null;
                }
            }
            if (!encDisp || !encMain) {
                newRoutes.push(r);
                continue;
            }

            const j0Arrival = j0.arrival != null ? Number(j0.arrival) : null;
            const jNArrival = jN.arrival != null ? Number(jN.arrival) : null;
            const vehicleGroupIdx = i;

            if (isEndBaseSplit) {
                const mainRoute = {
                    routeIndex: newRoutes.length,
                    vehicleId: r.vehicleId != null ? r.vehicleId : null,
                    stops: allStopsOnMainRoute,
                    hasStartbase: false,
                    hasEndbase: false,
                    _vehicleGroupIndex: vehicleGroupIdx,
                    startArrivalSec: r.startArrivalSec,
                    endArrivalSec: jNArrival,
                    geometry: encMain,
                    distance: mainDist,
                    durationSec: mainDur,
                    direction: r.direction,
                };
                const dispRoute = {
                    routeIndex: newRoutes.length + 1,
                    vehicleId: r.vehicleId != null ? r.vehicleId : null,
                    stops: [lastStop],
                    hasStartbase: false,
                    hasEndbase: false,
                    _displacementLeg: true,
                    _endbaseDisplacementLeg: true,
                    _vehicleGroupIndex: vehicleGroupIdx,
                    _startbaseFullVehicleDurationSec: origDur > 0 ? origDur : null,
                    _etaDepartureRouteIndex: newRoutes.length,
                    startArrivalSec: jNArrival,
                    endArrivalSec: r.endArrivalSec,
                    geometry: encDisp,
                    distance: dispDist,
                    durationSec: dispDur,
                    direction: this.$t('routePlanning.direction.displacement'),
                };
                newRoutes.push(mainRoute, dispRoute);
                continue;
            }

            const dispEndArrival = j0Arrival;

            const dispRoute = {
                routeIndex: newRoutes.length,
                vehicleId: r.vehicleId != null ? r.vehicleId : null,
                stops: [firstStop],
                hasStartbase: false,
                _displacementLeg: true,
                _vehicleGroupIndex: vehicleGroupIdx,
                _startbaseFullVehicleDurationSec: origDur > 0 ? origDur : null,
                startArrivalSec: r.startArrivalSec,
                endArrivalSec: dispEndArrival,
                geometry: encDisp,
                distance: dispDist,
                durationSec: dispDur,
                direction: this.$t('routePlanning.direction.displacement'),
            };

            const mainRoute = {
                routeIndex: newRoutes.length + 1,
                vehicleId: r.vehicleId != null ? r.vehicleId : null,
                stops: allStopsOnMainRoute,
                hasStartbase: false,
                _startbaseMainContinuation: true,
                _vehicleGroupIndex: vehicleGroupIdx,
                _etaDepartureRouteIndex: newRoutes.length,
                startArrivalSec: j0Arrival,
                endArrivalSec: r.endArrivalSec,
                geometry: encMain,
                distance: mainDist,
                durationSec: mainDur,
                direction: r.direction,
            };

            newRoutes.push(dispRoute, mainRoute);
        }

        if (newRoutes.length !== responseData.routes.length) {
            responseData.routes = newRoutes;
        }
        (responseData.routes || []).forEach(seg => {
            if (seg && seg.versoRoute != null) delete seg.versoRoute;
        });
    },
    /** Chave string estável para deduplicar paradas/cercas por coordenada (6 casas decimais). */
    getLatLngKey(lat, lng) {
        if (lat == null || lng == null) return null;
        return `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
    },
};

module.exports = {
    routingMethods,
    ROUTING_CONFIG_FIELDS,
    ROUTING_LABEL_KEYS,
    CONCURRENT_GROUP_VEHICLE_ID_SPAN,
    VERSO_PLAN_AUX_WAIT_MS,
    parseDurationSeconds,
    isPositiveInt,
    parseLatLngPair,
    jobServiceSeconds,
    haversineMeters,
};
